<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="refPfiOnboarding"
      :items="pfiOnboardingList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records found"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>
      <template #cell(operatingLicenseIssueDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>
      <template #cell(operationCommencementDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>
      <template #cell(financialInstitutionType)="data">
        {{ addSpaceToString(data.value) }}
      </template>
      <template #cell(shareholdersFund)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_PFI_ONBOARDING')"
              @click="selectPFIOnboardingItems(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Modify</span>
            </b-dropdown-item>
            <b-dropdown-item @click="selectPFIOnboardingItems(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">View</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.onboardingStages.DueDiligence && getUserPermission('SUBMIT_DUE_DILIGENCE')"
              @click="onViewDueDiligence(data.item, $event.target)"
            >
              <feather-icon icon="ClipboardIcon" />
              <span class="align-middle ml-50">Due Diligence</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.onboardingStages.DueDiligence && data.item.onboardingStages.CreditLimit && getUserPermission('SETUP_CREDIT_LIMIT')"
              @click="manageCreditLimit(data.item, $event.target)"
            >
              <feather-icon icon="CreditCardIcon" />
              <span class="align-middle ml-50">Credit Limit</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.onboardingStages.DueDiligence && data.item.onboardingStages.CreditLimit && data.item.onboardingStages.OfferLetter"
              @click="downloadOfferLetters(data.item, $event.target)"
            >
              <feather-icon icon="DownloadIcon" />
              <span class="align-middle ml-50">Download Offer Letter</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.onboardingStages.DueDiligence && data.item.onboardingStages.CreditLimit && data.item.onboardingStages.OfferLetter && data.item.onboardingStages.KYC"
              @click="fetchKYCInformation(data.item, $event.target)"
            >
              <feather-icon icon="UserCheckIcon" />
              <span class="align-middle ml-50">KYC Information</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.onboardingStages.DueDiligence && data.item.onboardingStages.CreditLimit && data.item.onboardingStages.OfferLetter && data.item.onboardingStages.KYC && data.item.onboardingStages.MasterFinancingAgreement"
              @click="performMFA(data.item, $event.target)"
            >
              <feather-icon icon="FilePlusIcon" />
              <span class="align-middle ml-50">Master Financing Agreement</span>
            </b-dropdown-item>
            <!-- <b-dropdown-item @click="uploadSupportingDocuments(data.item, $event.target)">
              <feather-icon icon="UploadIcon" />
              <span class="align-middle ml-50">Supporting Documents</span>
            </b-dropdown-item> -->
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      no-close-on-backdrop
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      title="Filters"
      modal-footer
      @ok="fetchPfiOnboarding"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.institutionName"
            label="Institution Name"
            placeholder="Enter institution name"
            type="text"
          />
        </b-col>
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="contact-email"
            v-model="filter.email"
            label="Contact Email Address"
            placeholder="Enter contact email address"
            type="text"
            error-name="Contact Email Address"
            rules="isEmailAddress"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="pfi-code"
            v-model="filter.partnerCode"
            label="PFI Code"
            placeholder="Enter PFI code"
            type="text"
          />
        </b-col>
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <b-modal
      id="upload-supporting-document-modal"
      ref="uploadSupportingDocumentModal"
      ok-title="Cancel"
      ok-only
      title="Supporting Documents"
      ok-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
    >
      <b-table
        ref="uploadDocListTable"
        select-mode="single"
        :items="uploadSupportingDocList"
        :busy="isKycBusy"
        responsive
        :fields="uploadSupportingDocListHeaders"
        primary-key="id"
        selectable
        show-empty
        empty-text="No data available"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
      >
        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>
        <template #cell(status)="data">
          <b-badge
            pill
            :variant="getStatusColor(data.value)"
          >
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(dateLastModified)="data">
          {{ data.value ? toDateWithYear(data.value) : '' }}
        </template>
        <template #cell(actions)="data">
          <Button
            label="Upload"
            size="sm"
            @click="uploadDocumentFile(data.item)"
          />
        </template>
      </b-table>
    </b-modal>

    <b-modal
      id="upload-document-file-modal"
      ref="uploadDocumentFileModal"
      :title="addSpaceToString(supportingDocumentType)"
      ok-title="Close"
      ok-only
      ok-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
    >
      <validation-observer ref="uploadRules">
        <b-form>
          <h6 class="section-label mb-1 mt-0 text-center">
            Document file upload
          </h6>
          <b-row class="match-height mb-2 mt-1 justify-content-center">
            <b-col
              md="6"
              class="d-flex flex-column align-items-center"
            >
              <Spinner :show="uploadLoadSpinner" />
              <div class="upload-image text-center">
                <b-form-group>
                  <b-form-file
                    id="upload-document"
                    v-model="documentImage"
                    :state="Boolean(documentImage)"
                    placeholder="Choose a file or drop it here..."
                    drop-placeholder="Drop file here..."
                    accept=".jpg,.jpeg,.png,.pdf"
                    plain
                    :disabled="isReadonly"
                    @change="onDocumentFileUpload"
                  />
                  <div
                    v-if="!hasDocumentImage"
                    class="upload-instruction"
                  >
                    <h4>Upload Document File</h4>
                    <p style="padding: 5px;">
                      Drag & drop file or choose from your device to upload
                    </p>
                    <p class="format">
                      Format accepted (jpg, png, jpeg, pdf)
                    </p>
                  </div>
                  <div v-else>
                    <div v-if="fileExtension === 'png'|| fileExtension === 'jpeg'|| fileExtension === 'jpg'">
                      <b-img
                        v-if="hasDocumentImage"
                        :src="documentImageSrc"
                        block
                        fluid
                        rounded
                      />
                    </div>
                    <div
                      v-if="fileExtension === 'pdf'"
                    >
                      <b-img
                        v-if="hasDocumentImage"
                        :src="pdfIcon"
                        block
                        rounded
                        width="250px"
                        height="250px"
                      />
                    </div>
                    <div
                      v-else
                    >
                      <b-img
                        src=""
                      />
                    </div>
                    <div
                      v-if="hasDocumentImage"
                      class="view-details"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="20"
                        class="text-primary"
                        @click="viewSupportingDocumentFile(documentImageSrc, supportingDocumentType, fileExtension)"
                      />
                    </div>
                  </div>
                </b-form-group>
              </div>
            </b-col>
            <b-col
              v-if="hasDocumentImage"
              md="3"
              class="mt-0"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="ml-0"
                @click="handleDownload(documentImageSrc)"
              >
                <feather-icon
                  icon="DownloadIcon"
                  class="mr-25"
                />
                <span>Download</span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>

    <b-modal
      id="view-avatar-modal"
      ref="avatarModal"
      :title="viewDocumentModalTitle"
      no-close-on-backdrop
      ok-title="Download File"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      @ok="handleDownload(viewDocumentData)"
    >
      <div class="m-2 text-center">
        <b-row>

          <b-col cols="12">
            <div v-if="fileExtension === 'png'|| fileExtension === 'jpeg'|| fileExtension === 'jpg'">
              <b-img
                :src="viewDocumentData"
                block
                fluid
                rounded
              />
            </div>
            <div
              v-if="fileExtension === 'pdf'"
            >
              <b-img
                :src="pdfIcon"
                block
                rounded
                width="250px"
                height="250px"
              />
            </div>
            <div
              v-else
            >
              <b-img
                src=""
              />
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <b-modal
      id="kyc-information-modal"
      ref="kycInformationModal"
      title="KYC Information"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="xl"
      hide-footer
      @hidden="resetKYCInformationForm"
    >

      <b-tabs v-model="activeTab">
        <b-tab
          title="Director's Information"
          active
        >
          <!-- v-if="isPageShow" -->
          <validation-observer
            ref="kycInformationRules"
          >
            <b-form>
              <div
                class="form-section"
              >
                <h5 class="text-primary mb-2 mt-3">
                  Director's Information
                </h5>
                <b-row>
                  <b-col md="4">
                    <SelectField
                      id="titles"
                      v-model="kycInformationFormData.title"
                      label="Title"
                      :options="titleOptions"
                      :reduce="option => option.value"
                      error-name="Title"
                      rules="required"
                      :readonly="isReadonly"
                      item="label"
                      placeholder="Select"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="lastname"
                      v-model="kycInformationFormData.lastName"
                      label="Last Name"
                      placeholder="Enter last name"
                      type="text"
                      error-name="Last Name"
                      rules="required"
                      :readonly="isKycReadonly"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="firstname"
                      v-model="kycInformationFormData.firstName"
                      label="First Name"
                      placeholder="Enter first name"
                      type="text"
                      error-name="First Name"
                      rules="required"
                      :readonly="isKycReadonly"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="middlename"
                      v-model="kycInformationFormData.middleName"
                      label="Middle Name"
                      placeholder="Enter middle name"
                      type="text"
                      :readonly="isKycReadonly"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="contact-phone-number"
                      v-model="kycInformationFormData.phoneNumber"
                      label="Contact Phone Number"
                      placeholder="Enter contact phone number"
                      type="number"
                      error-name="Contact Phone Number"
                      rules="required|phoneNumber"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="email-address"
                      v-model="kycInformationFormData.emailAddress"
                      label="Email Address"
                      placeholder="Enter email address"
                      type="text"
                      error-name="Email Address"
                      rules="required|isEmailAddress"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="bvn"
                      v-model="kycInformationFormData.bvn"
                      label="BVN"
                      placeholder="Enter your bvn"
                      type="number"
                      error-name="BVN"
                      rules="required|max: 11"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col md="4">
                    <SelectField
                      id="pep"
                      v-model="kycInformationFormData.pep"
                      label="PEP"
                      :options="pepOptions"
                      :reduce="option => option.value"
                      error-name="PEP"
                      rules="required"
                      item="label"
                      placeholder="Select"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="form-section mt-3">
                  <h5 class="text-primary mb-2">
                    Valid Identification Details
                  </h5>
                  <b-row>

                    <b-col md="4">
                      <SelectField
                        id="means-of-id"
                        v-model="kycInformationFormData.meansOfIdentification"
                        label="Means of Identification"
                        :options="meansOfIdOptions"
                        :reduce="option => option.value"
                        item="label"
                        error-name="Means of Identification"
                        rules="required"
                        placeholder="Select"
                        :readonly="isReadonly"
                      />
                    </b-col>
                    <b-col md="4">
                      <InputField
                        id="id-number"
                        v-model="kycInformationFormData.idNumber"
                        label="Means of Identification Number"
                        placeholder="Enter identification number on your card"
                        type="text"
                        error-name="Means of Identification Number"
                        rules="required"
                        :readonly="isReadonly"
                      />
                    </b-col>

                    <b-col md="4">
                      <DatePickerField
                        id="issue-date"
                        v-model="kycInformationFormData.meansOfIdentificationIssueDate"
                        label="Means of Identification Issue Date"
                        format="Y-m-d"
                        :config="datePickerConfig"
                        :disabled="isReadonly"
                        error-name="Means of Identification Issue Date"
                        rules="required"
                      />
                    </b-col>
                    <b-col md="4">
                      <DatePickerField
                        id="expiry-date"
                        v-model="kycInformationFormData.meansOfIdentificationExpiryDate"
                        label="Means of Identification Expiry Date"
                        format="Y-m-d"
                        :min-date="new Date()"
                        :config="dynamicMeanOfIDdatePickerConfig"
                        :disabled="isReadonly"
                        error-name="Means of Identification Expiry Date"
                        rules="required"
                      />
                    </b-col>

                    <b-col md="4">
                      <InputField
                        id="issuing-authority"
                        v-model="kycInformationFormData.meansOfIdentificationPlaceOfIssue"
                        label="Issuing Authority"
                        placeholder="Enter Issuing Authority"
                        type="text"
                        error-name="Issuing Authority"
                        rules="required"
                        :readonly="isReadonly"
                        :is-allow-special-characters="false"
                      />
                    </b-col>
                    <b-col
                      v-if="kycDirectorFile.validIDStatus"
                      md="4"
                      class="mt-2"
                    >
                      Valid ID Status:
                      <b-badge
                        pill
                        :variant="getStatusColor(kycDirectorFile.validIDStatus)"
                      >
                        {{ kycDirectorFile.validIDStatus }}
                      </b-badge>
                    </b-col>
                  </b-row>
                  <!-- Valid ID -->
                  <b-row>
                    <b-col
                      md="12"
                      class="mb-2 mt-3"
                    >
                      <h5 class="text-primary">
                        Upload a Valid ID
                      </h5>
                    </b-col>
                    <b-col
                      sm="12"
                      md="6"
                      lg="4"
                      class="mt-0"
                    >
                      <!-- <b-card> -->
                      <b-row class="match-height ml-1">
                        <b-col
                          md="8"
                          class="upload-image"
                        >
                          <Spinner :show="!!kycDirectorFile.loading" />

                          <validation-provider
                            v-slot="{ errors }"
                            :name="'Valid ID Document'"
                            :rules="fileRules"
                          >
                            <b-form-group
                              :state="!errors.length"
                              :invalid-feedback="errors[0] || kycDirectorFile.validationError"
                            >
                              <b-form-file
                                v-model="kycDirectorFile.file"
                                :state="!errors.length && !kycDirectorFile.validationError"
                                placeholder="Choose a file or drop it here..."
                                drop-placeholder="Drop file here..."
                                accept=".jpg,.jpeg,.png, .docx,.pdf, application/pdf, .xlsx, .csv"
                                plain
                                :disabled="isReadonly"
                                @change="onKycDocumentFileUpload"
                              />

                              <!-- Instructions -->
                              <div
                                v-if="!kycDirectorFile.url"
                                class="upload-instruction text-center"
                              >
                                <h4>Upload a valid ID</h4>
                                <p>Drag & drop file or choose from your device to upload</p>
                                <p class="format">
                                  Format accepted: JPG, PNG, JPEG, DOCX, PDF, XLSX, CSV
                                </p>
                              </div>

                              <!-- Preview -->
                              <div v-else>
                                <b-img
                                  v-if="['png','jpeg','jpg','bmp'].includes(kycDirectorFile.fileExtension)"
                                  :src="kycDirectorFile.url"
                                  block
                                  rounded
                                  width="250"
                                  height="250"
                                />
                                <b-img
                                  v-else-if="kycDirectorFile.fileExtension === 'pdf'"
                                  :src="pdfIcon"
                                  block
                                  rounded
                                  width="250"
                                  height="250"
                                />
                                <b-img
                                  v-else-if="kycDirectorFile.fileExtension === 'csv'"
                                  :src="csvIcon"
                                  block
                                  rounded
                                  width="250"
                                  height="250"
                                />
                                <b-img
                                  v-else-if="kycDirectorFile.fileExtension === 'xlsx'"
                                  :src="excelIcon"
                                  block
                                  rounded
                                  width="250"
                                  height="250"
                                />
                                <b-img
                                  v-else-if="kycDirectorFile.fileExtension === 'docx'"
                                  :src="wordIcon"
                                  block
                                  rounded
                                  width="250"
                                  height="250"
                                />
                                <b-img
                                  v-else
                                  src=""
                                />

                                <div
                                  class="view-details"
                                >
                                  <feather-icon
                                    icon="EyeIcon"
                                    size="20"
                                    class="text-primary"
                                    @click="onViewDocumentFile(kycDirectorFile.url, kycInformationFormData.meansOfIdentification)"
                                  />
                                </div>
                              </div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                        <b-col
                          md="4"
                          class="mt-0"
                        >
                          <b-row>
                            <!-- Remove Button -->
                            <b-col
                              v-if="kycDirectorFile.url"
                              md="12"
                              class="mt-1"
                            >
                              <b-button
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                variant="primary"
                                class="ml-0"
                                @click="removeKycDocument(kycDirectorFile.documentId, kycDirectorFile.type)"
                              >
                                <feather-icon
                                  icon="XIcon"
                                  class="mr-25"
                                />
                                <span>Remove</span>
                              </b-button>
                            </b-col>
                            <b-col
                              v-if="kycDirectorFile.url"
                              md="12"
                              class="mt-1"
                            >
                              <b-button
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                variant="primary"
                                class="ml-0"
                                @click="handleDownload(kycDirectorFile.url)"
                              >
                                <feather-icon
                                  icon="DownloadIcon"
                                  class="mr-25"
                                />
                                <span>Download</span>
                              </b-button>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>
                      <!-- </b-card> -->
                    </b-col>
                  </b-row>
                </div>

                <b-row
                  v-if="!isReadonly"
                  class="mt-3"
                >
                  <b-col
                    md="12"
                    class="text-center"
                  >
                    <Button
                      size="md"
                      :label="directorButtonLabel"
                      @click="addNewKYCDirectors"
                    />
                  </b-col>
                </b-row>

              </div>
            </b-form>
          </validation-observer>
          <div>
            <h5 class="text-primary mt-3 mb-1">
              Director's List
            </h5>
            <b-table
              ref="kycInformationListTable"
              select-mode="single"
              :items="kycInformationList"
              :busy="isKycBusy"
              :per-page="pageSize"
              :current-page="currentPage"
              responsive
              :fields="kycInformationHeaders"
              primary-key="id"
              selectable
              show-empty
              empty-text="No data available"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              class="position-relative mt-3"
            >
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner
                    class="align-middle"
                    variant="primary"
                  />
                </div>
              </template>
              <template #cell(SN)="data">
                {{ getOverallIndex(data.index) }}
              </template>
              <template #cell(pep)="data">
                <b-badge
                  pill
                  :variant="getStatusColor(data.value === true ? 'Yes' : 'No')"
                >
                  {{ data.value === true ? 'Yes' : 'No' }}
                </b-badge>
              </template>
              <template #cell(status)="data">
                <b-badge
                  pill
                  :variant="getStatusColor(data.value)"
                >
                  {{ data.value }}
                </b-badge>
              </template>

              <template #cell(actions)="data">
                <div class="d-flex align-items-center">
                  <div class="text-nowrap mr-2">
                    <Button
                      v-if="getUserPermission('VALIDATE_BVN')"
                      class=""
                      size="sm"
                      label="Validate BVN"
                      @click="bvnValidateAction(data.item)"
                    />
                  </div>

                  <div class="text-nowrap">
                    <b-dropdown
                      variant="link"
                      toggle-class="p-0"
                      no-caret
                      :right="$store.state.appConfig.isRTL"
                    >
                      <template #button-content>
                        <feather-icon
                          icon="MoreVerticalIcon"
                          size="16"
                          class="align-middle text-body"
                        />
                      </template>
                      <b-dropdown-item
                        v-if="getUserPermission('MODIFY_PFI_ONBOARDING')"
                        @click="selectKYCInformationItems(data.item, $event.target, data.index, 'edit')"
                      >
                        <feather-icon icon="EditIcon" />
                        <span class="align-middle ml-50">Modify</span>
                      </b-dropdown-item>
                      <b-dropdown-item
                        @click="selectKYCInformationItems(data.item, $event.target, data.index, 'view')"
                      >
                        <feather-icon icon="EyeIcon" />
                        <span class="align-middle ml-50">View</span>
                      </b-dropdown-item>
                      <b-dropdown-item
                        @click="deleteKYCDirector(data.item)"
                      >
                        <feather-icon icon="TrashIcon" />
                        <span class="align-middle ml-50">Remove Director</span>
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </div>

              </template>
            </b-table>
          </div>
        </b-tab>
        <b-tab
          title="Bank Information"
        >
          <validation-observer ref="institutionDocumentRules">
            <b-form>
              <div class="form-section">
                <b-row>
                  <b-col md="4">
                    <InputField
                      id="bank-name"
                      v-model="kycBankFormData.bankName"
                      label="Institution Name"
                      placeholder="Enter bank name"
                      type="text"
                      error-name="Institution Name"
                      rules="required"
                      readonly
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="institution-email-address"
                      v-model="kycBankFormData.institutionEmail"
                      label="Institution Email Address"
                      placeholder="Enter institution email address"
                      type="text"
                      error-name="Institution Email Address"
                      rules="required"
                      readonly
                    />
                  </b-col>
                  <b-col md="4">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <label
                        for="tin"
                      >TIN</label>
                      <small class="text-muted">Format: 00000000-0000</small>
                    </div>
                    <InputField
                      id="tin"
                      v-model="kycBankFormData.tin"
                      label=""
                      style="margin-top: -35px !important;"
                      placeholder="Enter your TIN"
                      type="text"
                      error-name="TIN"
                      rules="required|validTIN"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col md="12">
                    <TextAreaField
                      id="institution-address"
                      v-model="kycBankFormData.institutionAddress"
                      label="Institution Address"
                      placeholder="Enter institution address"
                      type="text"
                      error-name="Institution Address"
                      rules="required"
                      rows="5"
                      readonly
                    />
                  </b-col>
                </b-row>
              </div>
              <b-row>
                <!-- Non-Disclosure Agreement -->
                <b-col
                  md="6"
                  class="mt-2"
                >
                  <b-card>
                    <b-row>
                      <b-col
                        md="8"
                        class=" mt-0 ml-5"
                      >
                        <h6 class="section-label mb-2">
                          Non-Disclosure Agreement (NDA)
                        </h6>
                        <Button
                          label="Download Non-Disclosure Agreement"
                          size="md"
                          icon="DownloadIcon"
                          show-icon
                          class="mb-1"
                          @click="downloadKYCDocumentTemplate('GetNDA')"
                        />
                      </b-col>
                    </b-row>
                    <b-col
                      md="9"
                      class="mt-0 ml-3"
                    >
                      <hr class="horizontal-line">
                    </b-col>

                    <b-row class="match-height mb-2 mt-1 ml-5">
                      <b-col
                        md="8"
                        class="upload-image"
                      >
                        <Spinner :show="!!nonDisclosureAgreementFile.loading" />

                        <validation-provider
                          v-slot="{ errors }"
                          :name="'Upload Non-Disclosure Agreement (NDA)'"
                          :rules="fileRules"
                        >
                          <b-form-group
                            :state="!errors.length"
                            :invalid-feedback="errors[0] || nonDisclosureAgreementFile.validationError"
                          >
                            <b-form-file
                              v-model="nonDisclosureAgreementFile.file"
                              :state="!errors.length && !nonDisclosureAgreementFile.validationError"
                              placeholder="Choose a file or drop it here..."
                              drop-placeholder="Drop file here..."
                              accept=".jpg,.jpeg,.png, .docx,.pdf, application/pdf, .xlsx, .csv"
                              plain
                              :disabled="isReadonly"
                              @change="handleKYCFileUpload($event, 'nonDisclosureAgreementFile', 'NonDisclosureAgreement')"
                            />

                            <!-- Instructions -->
                            <div
                              v-if="!nonDisclosureAgreementFile.url"
                              class="upload-instruction text-center"
                            >
                              <h4>Upload Non-Disclosure Agreement (NDA)</h4>
                              <p>Drag & drop file or choose from your device to upload</p>
                              <p class="format">
                                Format accepted: JPG, PNG, JPEG, PDF, DOCX, XLSX, CSV
                              </p>
                            </div>

                            <!-- Preview -->
                            <div v-else>
                              <b-img
                                v-if="['png','jpeg','jpg','bmp'].includes(nonDisclosureAgreementFile.fileExtension)"
                                :src="nonDisclosureAgreementFile.url"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="nonDisclosureAgreementFile.fileExtension === 'pdf'"
                                :src="pdfIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="nonDisclosureAgreementFile.fileExtension === 'docx'"
                                :src="wordIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="nonDisclosureAgreementFile.fileExtension === 'xlsx'"
                                :src="excelIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="nonDisclosureAgreementFile.fileExtension === 'csv'"
                                :src="csvIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else
                                src=""
                              />

                              <div
                                class="view-details"
                              >
                                <feather-icon
                                  icon="EyeIcon"
                                  size="20"
                                  class="text-primary"
                                  @click="onViewDocumentFile(nonDisclosureAgreementFile.url, 'Non-Disclosure Agreement')"
                                />
                              </div>
                            </div>
                          </b-form-group>
                        </validation-provider>
                      </b-col>
                      <b-col
                        md="3"
                        class="mt-0"
                      >
                        <b-row>
                          <!-- Remove Button -->
                          <b-col
                            v-if="nonDisclosureAgreementFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="removeKycDocument(nonDisclosureAgreementFile.documentId, 'NonDisclosureAgreement')"
                            >
                              <feather-icon
                                icon="XIcon"
                                class="mr-25"
                              />
                              <span>Remove</span>
                            </b-button>
                          </b-col>
                          <b-col
                            v-if="nonDisclosureAgreementFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="handleDownload(nonDisclosureAgreementFile.url)"
                            >
                              <feather-icon
                                icon="DownloadIcon"
                                class="mr-25"
                              />
                              <span>Download</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>
                <!-- Non-Disclosure Agreement -->

                <!-- Direct Debit Mandate -->
                <b-col
                  md="6"
                  class="mt-2"
                >
                  <b-card>
                    <b-row>
                      <b-col
                        md="8"
                        class=" mt-0 ml-5"
                      >
                        <h6 class="section-label mb-2">
                          Direct Debit Mandate
                        </h6>
                        <Button
                          label="Download Direct Debit Mandate"
                          size="md"
                          icon="DownloadIcon"
                          show-icon
                          class="mb-1"
                          @click="downloadKYCDocumentTemplate('GetDirectDebitMandate')"
                        />
                      </b-col>
                    </b-row>
                    <b-col
                      md="9"
                      class="mt-0 ml-3"
                    >
                      <hr class="horizontal-line">
                    </b-col>

                    <b-row class="match-height mb-2 mt-1 ml-5">
                      <b-col
                        md="8"
                        class="upload-image"
                      >
                        <Spinner :show="!!directDebitMandateFile.loading" />

                        <validation-provider
                          v-slot="{ errors }"
                          :name="'Upload Direct Debit Mandate'"
                          :rules="fileRules"
                        >
                          <b-form-group
                            :state="!errors.length"
                            :invalid-feedback="errors[0] || directDebitMandateFile.validationError"
                          >
                            <b-form-file
                              v-model="directDebitMandateFile.file"
                              :state="!errors.length && !directDebitMandateFile.validationError"
                              placeholder="Choose a file or drop it here..."
                              drop-placeholder="Drop file here..."
                              accept=".jpg,.jpeg,.png, .docx,.pdf, application/pdf, .xlsx, .csv"
                              plain
                              :disabled="isReadonly"
                              @change="handleKYCFileUpload($event, 'directDebitMandateFile', 'DueDiligenceMandate')"
                            />

                            <!-- Instructions -->
                            <div
                              v-if="!directDebitMandateFile.url"
                              class="upload-instruction text-center"
                            >
                              <h4>Upload Direct Debit Mandate</h4>
                              <p>Drag & drop file or choose from your device to upload</p>
                              <p class="format">
                                Format accepted: JPG, PNG, JPEG, DOCX, PDF, XLSX, CSV
                              </p>
                            </div>

                            <!-- Preview -->
                            <div v-else>
                              <b-img
                                v-if="['png','jpeg','jpg','bmp'].includes(directDebitMandateFile.fileExtension)"
                                :src="directDebitMandateFile.url"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="directDebitMandateFile.fileExtension === 'pdf'"
                                :src="pdfIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="directDebitMandateFile.fileExtension === 'docx'"
                                :src="wordIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="directDebitMandateFile.fileExtension === 'xlsx'"
                                :src="excelIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="directDebitMandateFile.fileExtension === 'csv'"
                                :src="csvIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else
                                src=""
                              />
                              <div
                                class="view-details"
                              >
                                <feather-icon
                                  icon="EyeIcon"
                                  size="20"
                                  class="text-primary"
                                  @click="onViewDocumentFile(directDebitMandateFile.url, 'Direct Debit Mandate')"
                                />
                              </div>
                            </div>
                          </b-form-group>
                        </validation-provider>
                      </b-col>
                      <b-col
                        md="3"
                        class="mt-0"
                      >
                        <b-row>
                          <!-- Remove Button -->
                          <b-col
                            v-if="directDebitMandateFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="removeKycDocument(directDebitMandateFile.documentId, 'DueDiligenceMandate')"
                            >
                              <feather-icon
                                icon="XIcon"
                                class="mr-25"
                              />
                              <span>Remove</span>
                            </b-button>
                          </b-col>
                          <b-col
                            v-if="directDebitMandateFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="handleDownload(directDebitMandateFile.url)"
                            >
                              <feather-icon
                                icon="DownloadIcon"
                                class="mr-25"
                              />
                              <span>Download</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>
                <!-- Direct Debit Mandate -->

                <!-- Anti-Money Laundering Questionnaire -->
                <b-col
                  md="6"
                  class="mt-2"
                >
                  <b-card>
                    <b-row>
                      <b-col
                        md="8"
                        class=" mt-0 ml-5"
                      >
                        <h6 class="section-label mb-2">
                          Anti-Money Laundering Questionnaire
                        </h6>
                        <Button
                          label="Download Anti-Money Laundering Questionnaire"
                          size="md"
                          icon="DownloadIcon"
                          show-icon
                          class="mb-1"
                          @click="downloadKYCDocumentTemplate('GetWolfsbergQuestionnaire')"
                        />
                      </b-col>
                    </b-row>
                    <b-col
                      md="9"
                      class="mt-0 ml-3"
                    >
                      <hr class="horizontal-line">
                    </b-col>

                    <b-row class="match-height mb-2 mt-1 ml-5">
                      <b-col
                        md="8"
                        class="upload-image"
                      >
                        <Spinner :show="!!antiMoneyLaunderingQuestionnaireFile.loading" />

                        <validation-provider
                          v-slot="{ errors }"
                          :name="'Anti-Money Laundering Questionnaire'"
                          :rules="fileRules"
                        >
                          <b-form-group
                            :state="!errors.length"
                            :invalid-feedback="errors[0] || antiMoneyLaunderingQuestionnaireFile.validationError"
                          >
                            <b-form-file
                              v-model="antiMoneyLaunderingQuestionnaireFile.file"
                              :state="!errors.length && !antiMoneyLaunderingQuestionnaireFile.validationError"
                              placeholder="Choose a file or drop it here..."
                              drop-placeholder="Drop file here..."
                              accept=".jpg,.jpeg,.png, .docx,.pdf, application/pdf, .xlsx, .csv"
                              plain
                              :disabled="isReadonly"
                              @change="handleKYCFileUpload($event, 'antiMoneyLaunderingQuestionnaireFile', 'WolfsbergQuestionnaire')"
                            />

                            <!-- Instructions -->
                            <div
                              v-if="!antiMoneyLaunderingQuestionnaireFile.url"
                              class="upload-instruction text-center"
                            >
                              <h4>Upload Anti-Money Laundering Questionnaira</h4>
                              <p>Drag & drop file or choose from your device to upload</p>
                              <p class="format">
                                Format accepted: JPG, PNG, JPEG, DOCX, PDF, XLSX, CSV
                              </p>
                            </div>

                            <!-- Preview -->
                            <div v-else>
                              <b-img
                                v-if="['png','jpeg','jpg','bmp'].includes(antiMoneyLaunderingQuestionnaireFile.fileExtension)"
                                :src="antiMoneyLaunderingQuestionnaireFile.url"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="antiMoneyLaunderingQuestionnaireFile.fileExtension === 'pdf'"
                                :src="pdfIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="antiMoneyLaunderingQuestionnaireFile.fileExtension === 'docx'"
                                :src="wordIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="antiMoneyLaunderingQuestionnaireFile.fileExtension === 'xlsx'"
                                :src="excelIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="antiMoneyLaunderingQuestionnaireFile.fileExtension === 'csv'"
                                :src="csvIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else
                                src=""
                              />
                              <div
                                class="view-details"
                              >
                                <feather-icon
                                  icon="EyeIcon"
                                  size="20"
                                  class="text-primary"
                                  @click="onViewDocumentFile(antiMoneyLaunderingQuestionnaireFile.url, 'Anti-Money Laundering Questionnaira')"
                                />
                              </div>
                            </div>
                          </b-form-group>
                        </validation-provider>
                      </b-col>
                      <b-col
                        md="3"
                        class="mt-0"
                      >
                        <b-row>
                          <!-- Remove Button -->
                          <b-col
                            v-if="antiMoneyLaunderingQuestionnaireFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="removeKycDocument(antiMoneyLaunderingQuestionnaireFile.documentId, 'WolfsbergQuestionnaire')"
                            >
                              <feather-icon
                                icon="XIcon"
                                class="mr-25"
                              />
                              <span>Remove</span>
                            </b-button>
                          </b-col>
                          <b-col
                            v-if="antiMoneyLaunderingQuestionnaireFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="handleDownload(antiMoneyLaunderingQuestionnaireFile.url)"
                            >
                              <feather-icon
                                icon="DownloadIcon"
                                class="mr-25"
                              />
                              <span>Download</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>
                <!-- Anti-Money Laundering Questionnaire -->

                <!--  Declaration of Ultimate Beneficial Ownership (UBO) -->
                <b-col
                  md="6"
                  class="mt-2"
                >
                  <b-card>
                    <b-row>
                      <b-col
                        md="8"
                        class=" mt-0 ml-5"
                      >
                        <h6 class="section-label mb-2">
                          Declaration of Ultimate Beneficial Ownership (UBO)
                        </h6>
                        <Button
                          label="Download Declaration of Ultimate Beneficial Ownership (UBO)"
                          size="md"
                          icon="DownloadIcon"
                          show-icon
                          class="mb-1"
                          @click="downloadKYCDocumentTemplate('GetDeclarationOfUBO')"
                        />
                      </b-col>
                    </b-row>
                    <b-col
                      md="9"
                      class="mt-0 ml-3"
                    >
                      <hr class="horizontal-line">
                    </b-col>

                    <b-row class="match-height mb-2 mt-1 ml-5">
                      <b-col
                        md="8"
                        class="upload-image"
                      >
                        <Spinner :show="!!declarationOfUltimateBeneficialOwnershipFile.loading" />

                        <validation-provider
                          v-slot="{ errors }"
                          :name="'Declaration of Ultimate Beneficial Ownership'"
                          :rules="fileRules"
                        >
                          <b-form-group
                            :state="!errors.length"
                            :invalid-feedback="errors[0] || declarationOfUltimateBeneficialOwnershipFile.validationError"
                          >
                            <b-form-file
                              v-model="declarationOfUltimateBeneficialOwnershipFile.file"
                              :state="!errors.length && !declarationOfUltimateBeneficialOwnershipFile.validationError"
                              placeholder="Choose a file or drop it here..."
                              drop-placeholder="Drop file here..."
                              accept=".jpg,.jpeg,.png, .docx,.pdf, application/pdf, .xlsx, .csv"
                              plain
                              :disabled="isReadonly"
                              @change="handleKYCFileUpload($event, 'declarationOfUltimateBeneficialOwnershipFile', 'DeclarationOfUBO')"
                            />

                            <!-- Instructions -->
                            <div
                              v-if="!declarationOfUltimateBeneficialOwnershipFile.url"
                              class="upload-instruction text-center"
                            >
                              <h4>Upload  Declaration of Ultimate Beneficial Ownership</h4>
                              <p>Drag & drop file or choose from your device to upload</p>
                              <p class="format">
                                Format accepted: JPG, PNG, JPEG, DOCX, PDF, XLSX, CSV
                              </p>
                            </div>

                            <!-- Preview -->
                            <div v-else>
                              <b-img
                                v-if="['png','jpeg','jpg','bmp'].includes(declarationOfUltimateBeneficialOwnershipFile.fileExtension)"
                                :src="declarationOfUltimateBeneficialOwnershipFile.url"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="declarationOfUltimateBeneficialOwnershipFile.fileExtension === 'pdf'"
                                :src="pdfIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="declarationOfUltimateBeneficialOwnershipFile.fileExtension === 'docx'"
                                :src="wordIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="declarationOfUltimateBeneficialOwnershipFile.fileExtension === 'xlsx'"
                                :src="excelIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="declarationOfUltimateBeneficialOwnershipFile.fileExtension === 'csv'"
                                :src="csvIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else
                                src=""
                              />
                              <div
                                class="view-details"
                              >
                                <feather-icon
                                  icon="EyeIcon"
                                  size="20"
                                  class="text-primary"
                                  @click="onViewDocumentFile(declarationOfUltimateBeneficialOwnershipFile.url, 'Declaration of Ultimate Beneficial Ownership')"
                                />
                              </div>
                            </div>
                          </b-form-group>
                        </validation-provider>
                      </b-col>
                      <b-col
                        md="3"
                        class="mt-0"
                      >
                        <b-row>
                          <!-- Remove Button -->
                          <b-col
                            v-if="declarationOfUltimateBeneficialOwnershipFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="removeKycDocument(declarationOfUltimateBeneficialOwnershipFile.documentId, 'DeclarationOfUBO')"
                            >
                              <feather-icon
                                icon="XIcon"
                                class="mr-25"
                              />
                              <span>Remove</span>
                            </b-button>
                          </b-col>
                          <b-col
                            v-if="declarationOfUltimateBeneficialOwnershipFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="handleDownload(declarationOfUltimateBeneficialOwnershipFile.url)"
                            >
                              <feather-icon
                                icon="DownloadIcon"
                                class="mr-25"
                              />
                              <span>Download</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>
                <!--  Declaration of Ultimate Beneficial Ownership (UBO) -->

                <!--   Signature Mandate-->
                <b-col
                  md="6"
                  class="mt-2"
                >
                  <b-card>
                    <b-row>
                      <b-col
                        md="8"
                        class=" mt-0 ml-5"
                      >
                        <h6 class="section-label mb-2">
                          Signature Mandate
                        </h6>
                        <Button
                          label="Download Signature Mandate"
                          size="md"
                          icon="DownloadIcon"
                          show-icon
                          class="mb-1"
                          @click="downloadKYCDocumentTemplate('GetSignatureMandate')"
                        />
                      </b-col>
                    </b-row>
                    <b-col
                      md="9"
                      class="mt-0 ml-3"
                    >
                      <hr class="horizontal-line">
                    </b-col>

                    <b-row class="match-height mb-2 mt-1 ml-5">
                      <b-col
                        md="8"
                        class="upload-image"
                      >
                        <Spinner :show="!! signatureMandateFile.loading" />

                        <validation-provider
                          v-slot="{ errors }"
                          :name="'Upload Signature Mandate'"
                          :rules="fileRules"
                        >
                          <b-form-group
                            :state="!errors.length"
                            :invalid-feedback="errors[0] || signatureMandateFile.validationError"
                          >
                            <b-form-file
                              v-model="signatureMandateFile.file"
                              :state="!errors.length && !signatureMandateFile.validationError"
                              placeholder="Choose a file or drop it here..."
                              drop-placeholder="Drop file here..."
                              accept=".jpg,.jpeg,.png, .docx,.pdf, application/pdf, .xlsx, .csv"
                              plain
                              :disabled="isReadonly"
                              @change="handleKYCFileUpload($event, 'signatureMandateFile', 'SignatureMandate')"
                            />

                            <!-- Instructions -->
                            <div
                              v-if="!signatureMandateFile.url"
                              class="upload-instruction text-center"
                            >
                              <h4>Upload Signature Mandate</h4>
                              <p>Drag & drop file or choose from your device to upload</p>
                              <p class="format">
                                Format accepted: PDF, JPG, PNG, JPEG, DOC, DOCX
                              </p>
                            </div>

                            <!-- Preview -->
                            <div v-else>
                              <b-img
                                v-if="['png','jpeg','jpg','bmp'].includes(signatureMandateFile.fileExtension)"
                                :src="signatureMandateFile.url"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="signatureMandateFile.fileExtension === 'pdf'"
                                :src="pdfIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="signatureMandateFile.fileExtension === 'docx'"
                                :src="wordIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="signatureMandateFile.fileExtension === 'xlsx'"
                                :src="excelIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="signatureMandateFile.fileExtension === 'csv'"
                                :src="csvIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else
                                src=""
                              />
                              <div
                                class="view-details"
                              >
                                <feather-icon
                                  icon="EyeIcon"
                                  size="20"
                                  class="text-primary"
                                  @click="onViewDocumentFile(signatureMandateFile.url, 'Signature Mandate')"
                                />
                              </div>
                            </div>
                          </b-form-group>
                        </validation-provider>
                      </b-col>
                      <b-col
                        md="3"
                        class="mt-0"
                      >
                        <b-row>
                          <!-- Remove Button -->
                          <b-col
                            v-if="signatureMandateFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="removeKycDocument(signatureMandateFile.documentId, 'SignatureMandate')"
                            >
                              <feather-icon
                                icon="XIcon"
                                class="mr-25"
                              />
                              <span>Remove</span>
                            </b-button>
                          </b-col>
                          <b-col
                            v-if="signatureMandateFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="handleDownload(signatureMandateFile.url)"
                            >
                              <feather-icon
                                icon="DownloadIcon"
                                class="mr-25"
                              />
                              <span>Download</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>
                <!--   Signature Mandate-->

                <!--   Board Resolution-->
                <b-col
                  md="6"
                  class="mt-2"
                >
                  <b-card>
                    <b-row>
                      <b-col
                        md="8"
                        class=" mt-0 ml-5"
                      >
                        <h6 class="section-label mb-2">
                          Board Resolution
                        </h6>
                        <Button
                          label="Download Board Resolution"
                          size="md"
                          icon="DownloadIcon"
                          show-icon
                          class="mb-1"
                          @click="downloadKYCDocumentTemplate('GetBoardResolutionTemplate')"
                        />
                      </b-col>
                    </b-row>
                    <b-col
                      md="9"
                      class="mt-0 ml-3"
                    >
                      <hr class="horizontal-line">
                    </b-col>

                    <b-row class="match-height mb-2 mt-1 ml-5">
                      <b-col
                        md="8"
                        class="upload-image"
                      >
                        <Spinner :show="!! boardResolutionFile.loading" />

                        <validation-provider
                          v-slot="{ errors }"
                          :name="'Upload Board Resolution'"
                          :rules="fileRules"
                        >
                          <b-form-group
                            :state="!errors.length"
                            :invalid-feedback="errors[0] || boardResolutionFile.validationError"
                          >
                            <b-form-file
                              v-model="boardResolutionFile.url"
                              :state="!errors.length && !boardResolutionFile.validationError"
                              placeholder="Choose a file or drop it here..."
                              drop-placeholder="Drop file here..."
                              accept=".jpg,.jpeg,.png, .docx,.pdf, application/pdf, .xlsx, .csv"
                              plain
                              :disabled="isReadonly"
                              @change="handleKYCFileUpload($event, 'boardResolutionFile', 'BoardResolutionTemplate')"
                            />

                            <!-- Instructions -->
                            <div
                              v-if="!boardResolutionFile.url"
                              class="upload-instruction text-center"
                            >
                              <h4>Upload Board Resolution</h4>
                              <p>Drag & drop file or choose from your device to upload</p>
                              <p class="format">
                                Format accepted: PDF, JPG, PNG, JPEG, DOC, DOCX
                              </p>
                            </div>

                            <!-- Preview -->
                            <div v-else>
                              <b-img
                                v-if="['png','jpeg','jpg','bmp'].includes(boardResolutionFile.fileExtension)"
                                :src="boardResolutionFile.url"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="boardResolutionFile.fileExtension === 'pdf'"
                                :src="pdfIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="boardResolutionFile.fileExtension === 'docx'"
                                :src="wordIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="boardResolutionFile.fileExtension === 'xlsx'"
                                :src="excelIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else-if="boardResolutionFile.fileExtension === 'csv'"
                                :src="csvIcon"
                                block
                                rounded
                                width="250"
                                height="250"
                              />
                              <b-img
                                v-else
                                src=""
                              />
                              <div
                                class="view-details"
                              >
                                <feather-icon
                                  icon="EyeIcon"
                                  size="20"
                                  class="text-primary"
                                  @click="onViewDocumentFile(boardResolutionFile.url, 'Board Resolution')"
                                />
                              </div>
                            </div>
                          </b-form-group>
                        </validation-provider>
                      </b-col>
                      <b-col
                        md="3"
                        class="mt-0"
                      >
                        <b-row>
                          <!-- Remove Button -->
                          <b-col
                            v-if="boardResolutionFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="removeKycDocument(boardResolutionFile.documentId, 'BoardResolutionTemplate')"
                            >
                              <feather-icon
                                icon="XIcon"
                                class="mr-25"
                              />
                              <span>Remove</span>
                            </b-button>
                          </b-col>
                          <b-col
                            v-if="boardResolutionFile.url"
                            md="12"
                            class="mt-1"
                          >
                            <b-button
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="primary"
                              class="ml-0"
                              @click="handleDownload(boardResolutionFile.url)"
                            >
                              <feather-icon
                                icon="DownloadIcon"
                                class="mr-25"
                              />
                              <span>Download</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>
                <!--   Board Resolution-->
              </b-row>
            </b-form>
          </validation-observer>
        </b-tab>
      </b-tabs>

      <!-- <KYCInformation
        ref="kycInformationComponent"
        :form-data="kycInformationFormData"
        :is-readonly="isReadonly"
        :table-items="kycInformationList"
        :is-busy="isKycBusy"
        :is-edit="isReadonly"
        :title-options="titleOptions"
        :pep-options="pepOptions"
        :gender-options="genderOptions"
        :marital-status-options="maritalStatusOptions"
        :employ-status-options="employStatusOption"
        :country-options="countryOptions"
        :state-options="stateOptions"
        :lga-options="lgaOptions"
        :means-of-id-options="meansOfIdOptions"

        show-bvn-validation
        @add-kyc-information="addKYCInformationToTable"
        @validate-bvn="bvnValidateAction"
        @row-action="selectKYCInformationItems"
        @get-lga-by-state="getLGAByState"
        @form-update="handleFormUpdate"
        @document-uploaded="handleDocumentUploaded"
      /> -->
      <!-- Custom footer -->
      <div class="w-100 d-flex justify-content-end">
        <b-button
          variant="outline-secondary"
          class="mr-2"
          @click="$refs.kycInformationModal.hide()"
        >
          Cancel
        </b-button>
        <b-button
          v-if="activeTab < 1"
          variant="primary"
          class="mr-2"
          @click="toggleBankNextTab"
        >
          Next &gt;
        </b-button>
        <b-button
          v-if="activeTab === 1"
          variant="outline-secondary"
          class="mr-2"
          @click="toggleBankPreviousTab"
        >
          &lt; Previous
        </b-button>
        <b-button
          v-if="activeTab === 1"
          variant="primary"
          @click="handleAllKYCInformation"
        >
          Submit
        </b-button>
      </div>
      <FullScreenSpinner :show="showSpinner" />
    </b-modal>

    <!-- due diligence modal -->
    <b-modal
      id="due-diligence-modal"
      ref="due-diligence-modal"
      :ok-title="$t('Submit')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="xl"
      no-close-on-backdrop
      :ok-disabled="isSubmittingDueDiligence"
      title="Due Diligence"
      @ok="handleDueDiligenceSubmit"
    >
      <b-tabs>
        <b-tab
          title="Compulsory Documents"
          active
        >
          <app-collapse
            class="business-items"
            type="margin"
            accordion
          >
            <app-collapse-item
              title="Legal Registration Documents"
              class="card-item px-2"
            >
              <validation-observer
                ref="legalDocumentsRules"
                @submit.prevent="saveLegalRegDocuments"
              >
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Evidence of CAC Registration (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Evidence of CAC Registration"
                        rules="required"
                      >
                        <b-form-file
                          id="evidence-file-name"
                          v-model="dueDiligence.compulsoryDocument.legalRegDocuments.cacRegistration"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CAC_REGISTRATION)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Memorandum and Article of Association (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Memorandum and Article of Association"
                        rules="required"
                      >
                        <b-form-file
                          id="memorandum-file-name"
                          v-model="dueDiligence.compulsoryDocument.legalRegDocuments.articleOfAssociation"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.ARTICLE_OF_ASSOCIATION)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>

                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Operating License of CBN (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Operating License of CBN"
                        rules="required"
                      >
                        <b-form-file
                          id="operating-license-name"
                          v-model="dueDiligence.compulsoryDocument.legalRegDocuments.operatingLicense"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv  format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.OPERATING_LICENSE)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>

                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.legalRegDocuments.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      :loading="isLoading"
                      :disabled="isDisabled"
                      @click="saveLegalRegDocuments"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="legalDocumentRef"
                :busy="isBusy"
                :items="legalDocumentList"
                responsive
                :fields="legalDocumentHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(evidenceOfCACRegistration)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.evidenceOfCACRegistration, 'Evidence Of CAC Registration')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(memorandum)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.memorandum, 'Memorandum')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(operatingLicense)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.operatingLicense, 'Operating License')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>

            <app-collapse-item
              title="Board and Management Information"
              class="card-item px-2"
            >
              <validation-observer ref="boardManagementRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="4"
                    >
                      <InputField
                        id="last-name"
                        v-model="dueDiligence.compulsoryDocument.boardManagement.lastName"
                        label="Last Name"
                        placeholder="Enter last name"
                        type="text"
                        error-name="Last Name"
                        rules="required"
                        for="last-name"
                        onpaste="return false;"
                        :is-allow-special-characters="false"
                      />
                    </b-col>
                    <b-col
                      md="4"
                    >
                      <InputField
                        id="first-name"
                        v-model="dueDiligence.compulsoryDocument.boardManagement.firstName"
                        label="First Name"
                        placeholder="Enter first name"
                        type="text"
                        error-name="First Name"
                        rules="required"
                        for="first-name"
                        onpaste="return false;"
                        :is-allow-special-characters="false"
                      />
                    </b-col>
                    <b-col
                      md="4"
                    >
                      <InputField
                        id="middle-name"
                        v-model="dueDiligence.compulsoryDocument.boardManagement.middleName"
                        label="Middle Name"
                        placeholder="Enter middile name"
                        type="text"
                        for="middle-name"
                        onpaste="return false;"
                        :is-allow-special-characters="false"
                      />
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Profile/CV Upload (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Profile/CV Upload"
                        rules="required"
                      >
                        <b-form-file
                          id="profile-CV-Upload-file-name"
                          v-model="dueDiligence.compulsoryDocument.boardManagement.cvUpload"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CV_UPLOAD)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        CBN Approval Evidence (Board/Management) (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="CBN Approval Evidence"
                        rules="required"
                      >
                        <b-form-file
                          id="CBN-approval-evidence-file-name"
                          v-model="dueDiligence.compulsoryDocument.boardManagement.cbnApprovalEvidence"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CBN_APPROVAL_EVIDENCE)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="4"
                    >
                      <InputField
                        id="designation"
                        v-model="dueDiligence.compulsoryDocument.boardManagement.designation"
                        label="Designation"
                        placeholder="Enter designation"
                        type="text"
                        error-name="Designation"
                        rules="required"
                        for="designation"
                        onpaste="return false;"
                        :is-allow-special-characters="false"
                      />
                    </b-col>
                    <b-col md="4">
                      <DatePickerField
                        id="year-appointed"
                        v-model="dueDiligence.compulsoryDocument.boardManagement.yearAppointed"
                        :min-date="new Date()"
                        label="Year Appointed"
                        error-name="Year Appointed"
                        rules="required"
                        :config="dueDiligenceDatePickerConfig"
                        placeholder="Year Appointed"
                        :on-change="onSelectYearAppointed"
                      />
                    </b-col>

                    <b-col
                      md="4"
                    >
                      <InputField
                        id="duration"
                        v-model="dueDiligence.compulsoryDocument.boardManagement.duration"
                        label="Duration"
                        placeholder=""
                        type="text"
                        for="duration"
                        onpaste="return false;"
                        readonly
                      />
                    </b-col>
                  </b-row>

                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.boardManagement.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>
                  <div class="w-100 d-flex justify-content-end">

                    <Button
                      label="Save"
                      size="md"
                      @click="saveBoardManagementInfo"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="boardManagementRef"
                :busy="isBusy"
                :items="boardManagementList"
                responsive
                :fields="boardManagementHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >

                <template #cell(cvUpload)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.cvUpload, 'CV Upload')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(cbnApprovalEvidence)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.cbnApprovalEvidence, 'CBN Approval Evidence')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>

            <app-collapse-item
              title="Leadership and Governance Structure"
              class="card-item px-2"
            >
              <validation-observer ref="leadershipStructureRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Bank Organisation Structure (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Bank Organisation Structure"
                        rules="required"
                      >
                        <b-form-file
                          id="bank-organisation-structure-file-name"
                          v-model="dueDiligence.compulsoryDocument.leadershipGovernance.bankOrganisationStructure"
                          label="Select File (.jpg, .jpeg, .png, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .jpeg, .png, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.BANK_ORGANISATION_STRUCTURE)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Bank's Organisational Chart (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Bank's Organisational Chart"
                        rules="required"
                      >
                        <b-form-file
                          id="bank-organisational-chart-file-name"
                          v-model="dueDiligence.compulsoryDocument.leadershipGovernance.bankOrganisationChart"
                          label="Select File (.jpg, .jpeg, .png, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .jpeg, .png, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.BANK_ORGANISATION_CHART)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.leadershipGovernance.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveLegalGovernance"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="leadershipAndGovernanceRef"
                :busy="isBusy"
                :items="leadershipAndGovernanceList"
                responsive
                :fields="leadershipAndGovernanceHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(bankOrganistionStructure)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.bankOrganistionStructure, 'Bank Organistion Structure')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(banksOrganisationalChart)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.banksOrganisationalChart, 'Banks Organisational Chart')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <app-collapse-item
              title="Financial Statements"
              class="card-item px-2"
            >
              <validation-observer ref="financialStatementRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Audited Financial Statement Year 2024 (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Audited Financial Statement Year 2024"
                        rules="required"
                      >
                        <b-form-file
                          id="audited-financial-statement-file-name-1"
                          v-model="dueDiligence.compulsoryDocument.financialStatements.auditedFinancialStatement1"
                          v-b-tooltip.hover.bottom="'You are required to upload the CBN approved version'"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.AUDITED_FINANCIAL_STATEMENT_1)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Audited Financial Statement Year 2023 (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Audited Financial Statement Year 2023"
                        rules="required"
                      >
                        <b-form-file
                          id="audited-financial-statement-file-name-2"
                          v-model="dueDiligence.compulsoryDocument.financialStatements.auditedFinancialStatement2"
                          v-b-tooltip.hover.bottom="'You are required to upload the CBN approved version'"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.AUDITED_FINANCIAL_STATEMENT_2)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Audited Financial Statement Year 2022 (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Audited Financial Statement Year 2022"
                        rules="required"
                      >
                        <b-form-file
                          id="audited-financial-statement-file-name-3"
                          v-model="dueDiligence.compulsoryDocument.financialStatements.auditedFinancialStatement3"
                          v-b-tooltip.hover.bottom="'You are required to upload the CBN approved version'"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.AUDITED_FINANCIAL_STATEMENT_3)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.financialStatements.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveFinancialStatements"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="financialStatementRef"
                :busy="isBusy"
                :items="financialStatementList"
                responsive
                :fields="financialStatementHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(auditedFinancialStatement1)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.auditedFinancialStatement1, 'Audited Financial Statement Year 2024')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(auditedFinancialStatement2)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.auditedFinancialStatement3, 'Audited Financial Statement Year 2023')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(auditedFinancialStatement3)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.auditedFinancialStatement3, 'Audited Financial Statement Year 2022')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>

            <!-- Recent Financial Performance -->
            <app-collapse-item
              title="Recent Financial Performance"
              class="card-item px-2"
            >
              <validation-observer ref="recentFinancialPerformanceRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Latest Quarterly Management Accounts & Prudential Ratios (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Latest Quarterly Management Accounts & Prudential Ratios"
                        rules="required"
                      >
                        <b-form-file
                          id="latest-financial-performance"
                          v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.latestFinancialPerformance"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.LASTEST_FINANCIAL_PERFORMANCE)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                    >
                      <ValidationObserver>
                        <InputNumberField
                          id="capital-adequacy-ratio"
                          v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.capitalAdequacyRatio"
                          label="Capital Adequacy Ratio (%)"
                          placeholder="Enter capital adequacy ratio"
                          type="number"
                          rules="required|max-value:100|positive"
                          error-name="Capital Adequacy Ratio"
                          for="capital-adequacy-ratio"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col
                      md="6"
                    >
                      <ValidationObserver>
                        <InputNumberField
                          id="non-performing-loan-ratio"
                          v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.nonPerformingLoanRatio"
                          label="Non Performing Loan Ratio (%)"
                          placeholder="Enter non performing loan ratio"
                          type="number"
                          rules="required|max-value:100|positive"
                          error-name="Non Performing Loan Ratio"
                          for="non-performing-loan-ratio"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col
                      md="6"
                    >
                      <ValidationObserver>
                        <InputNumberField
                          id="liquidity-ratio"
                          v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.liquidityRatio"
                          label="Liquidity Ratio (%)"
                          placeholder="Enter liquidity ratio"
                          type="number"
                          rules="required|max-value:100|positive"
                          error-name="Liquidity Ratio"
                          for="liquidity-ratio"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col
                      md="6"
                    >
                      <ValidationObserver>
                        <InputNumberField
                          id="loan to deposit ratio"
                          v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.loanToDepositRatio"
                          label="Loan To Deposit Ratio (%)"
                          placeholder="Enter loan to deposit ratio"
                          type="number"
                          rules="required|max-value:100|positive"
                          error-name="Loan To Deposit Ratio"
                          for="loan to deposit ratio"
                        />
                      </ValidationObserver>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveRecentFinancialPerformance"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="recentFinancialPerformanceRef"
                :busy="isBusy"
                :items="recentFinancialPerformanceList"
                responsive
                :fields="recentFinancialPerformanceHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(latestFinancialPerformance)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.latestFinancialPerformance, 'Latest Financial Performance')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Recent Financial Performance -->

            <!-- Credit Risk Management -->
            <app-collapse-item
              title="Credit Risk Management"
              class="card-item px-2"
            >
              <validation-observer ref="creditRiskManagementRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Credit Risk Strategy and Annual Portfolio Plan (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Credit Risk Strategy"
                        rules="required"
                      >
                        <b-form-file
                          id="credit-risk-strategy"
                          v-model="dueDiligence.compulsoryDocument.creditRiskManagement.creditRiskStrategyAndAnnualPortfolioPlan"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CREDIT_RISK_MANAGEMENT)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.creditRiskManagement.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveCreditRiskManagement"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="creditRiskManagementRef"
                :busy="isBusy"
                :items="creditRiskManagementList"
                responsive
                :fields="creditRiskManagementHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(creditRiskStrategyAndAnnualPortfolioPlan)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.creditRiskStrategyAndAnnualPortfolioPlan, 'Credit Risk Strategy And Annual Portfolio Plan')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>

            <!-- Risk Management Framework -->
            <app-collapse-item
              title="Risk Management Framework"
              class="card-item px-2"
            >
              <validation-observer ref="riskManagementFrameworkRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Risk Management Framework (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Risk Management Framework"
                        rules="required"
                      >
                        <b-form-file
                          id="risk-managment-framework"
                          v-model="dueDiligence.compulsoryDocument.riskManagementFramework.riskManagementFrameworkUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_FRAMEWORK)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Policies (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Policies"
                        rules="required"
                      >
                        <b-form-file
                          id="policies"
                          v-model="dueDiligence.compulsoryDocument.riskManagementFramework.policiesUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_POLICIES)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Limits (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Limits"
                        rules="required"
                      >
                        <b-form-file
                          id="limits"
                          v-model="dueDiligence.compulsoryDocument.riskManagementFramework.limitsUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_LIMITS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Manuals (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Manuals"
                        rules="required"
                      >

                        <b-form-file
                          id="manuals"
                          v-model="dueDiligence.compulsoryDocument.riskManagementFramework.ManualUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_MANUAL)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.riskManagementFramework.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveRiskManagementFramework"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="riskManagementFrameworkRef"
                :busy="isBusy"
                :items="riskManagementFrameworkList"
                responsive
                :fields="riskManagementFrameworkHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(riskManagementFrameworkUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.riskManagementFrameworkUrl, 'Risk Management Framework')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(policiesUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.policiesUrl, 'policies')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(limitsUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.limitsUrl, 'Limits')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(manualUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.manualUrl, 'Manuals')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Risk Management Framework -->

            <!-- Loan Portfolio Details -->
            <app-collapse-item
              title="Loan Portfolio Details"
              class="card-item px-2"
            >
              <validation-observer ref="loanPortfolioDetailsRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Most Recent Credit Portfolio Report (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Most Recent Credit Portfolio Report"
                        rules="required"
                      >

                        <b-form-file
                          id="loan-portfolio-details"
                          v-model="dueDiligence.compulsoryDocument.loanPortfolioDetails.creditPortfolioReport"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CREDIT_PORTFOLIO_REPORT)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.loanPortfolioDetails.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveLoanPortfolioDetails"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="loanPortfolioDetailsRef"
                :busy="isBusy"
                :items="loanPortfolioDetailsList"
                responsive
                :fields="loanPortfolioDetailsHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(creditPortfolioReport)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.creditPortfolioReport, 'Credit Portfolio Report')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Loan Portfolio Details -->

            <!-- AML/CFT Compliance -->
            <app-collapse-item
              title="AML/CFT Compliance"
              class="card-item px-2"
            >
              <validation-observer ref="AML_CFTComplianceRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Anti-Money Laundering (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Anti-Money Laundering"
                        rules="required"
                      >
                        <b-form-file
                          id="anti-money-laundry"
                          v-model="dueDiligence.compulsoryDocument.aMLCFTCompliance.antiMoneyLaundryUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.ANTI_MONEY_LAUNDRY)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Counter-Terrorist Financing Policies And Procedures (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Counter-Terrorist Financing Policies And Procedures"
                        rules="required"
                      >
                        <b-form-file
                          id="counter-terrorist-financing"
                          v-model="dueDiligence.compulsoryDocument.aMLCFTCompliance.CounterTerroristFinancingPoliciesUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.COUNTER_TERRORIST_FINANCING)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>

                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.aMLCFTCompliance.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveAMLCFTCompliance"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="aMLCFTComplianceRef"
                :busy="isBusy"
                :items="aMLCFTComplianceList"
                responsive
                :fields="aMLCFTComplianceHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(antiMoneyLaundryUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.antiMoneyLaundryUrl, 'Anti-Money Laundering')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(counterTerroristFinancingPoliciesUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.counterTerroristFinancingPoliciesUrl, 'Counter Terrorist Financing Policies')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- AML/CFT Compliance -->

            <!-- Regulatory Compliance -->
            <app-collapse-item
              title="Regulatory Compliance"
              class="card-item px-2"
            >
              <validation-observer ref="regulatoryComplianceRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Evidence of Regulatory Returns Rendition (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Evidence of Regulatory Returns Rendition"
                        rules="required"
                      >
                        <b-form-file
                          id="regulatory-returns-rendition"
                          v-model="dueDiligence.compulsoryDocument.regulatoryCompliance.regulatoryReturnsRenditionUrl"
                          label="Select File (jpg, png, jpeg, docx, pdf, xlsx, csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept="jpg, png, jpeg, docx, pdf, xlsx, csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.REGULATORY_RETURNS_RENDITION)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.regulatoryCompliance.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveRegulatoryCompliance"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="regulatoryComplianceRef"
                :busy="isBusy"
                :items="regulatoryComplianceList"
                responsive
                :fields="regulatoryComplianceHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(regulatoryReturnsRenditionUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.regulatoryReturnsRenditionUrl, 'Regulatory Returns Rendition')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Regulatory Compliance -->

            <!-- Regulatory Examination Reports -->
            <app-collapse-item
              title="Regulatory Examination Reports"
              class="card-item px-2"
            >
              <validation-observer ref="regulatoryExaminationReportsRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        CBN Examination Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="CBN Examination Reports"
                        rules="required"
                      >
                        <b-form-file
                          id="cbn-examination-reports"
                          v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.cBNExaminationReportsUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CBN_EXAMINATION_REPORTS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        NDIC Examination Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="NDIC Examination Reports"
                        rules="required"
                      >
                        <b-form-file
                          id="ndic-examination-reports"
                          v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.nDICExaminationReportUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          error-name="NDIC Examination Reports"
                          rules="required"
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.NDIC_EXAMINATION_REPORTS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Management Response to CBN Examination Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Management Response to CBN Examination Reports"
                        rules="required"
                      >
                        <b-form-file
                          id="management-response-to-examination-report"
                          v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.managementResponseToExaminationReportUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.MANAGEMENT_RESPONSE_TO_EXAMINATION_REPORTS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Management Response to NDIC Examination Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Management Response to NDIC Examination Reports"
                        rules="required"
                      >
                        <b-form-file
                          id="management-response-to-ndic-examination-report"
                          v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.managementResponseToNDICExaminationReportsUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.MANAGEMENT_RESPONSE_TO_NDIC_EXAMINATION_REPORTS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveRegulatoryExaminationReports"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="regulatoryExaminationReportsRef"
                :busy="isBusy"
                :items="regulatoryExaminationReportsList"
                responsive
                :fields="regulatoryExaminationReportsHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(cBNExaminationReportsUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.cBNExaminationReportsUrl, 'CBN Examination Reports')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(nDICExaminationReportUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.nDICExaminationReportUrl, 'NDIC Examination Report')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(managementResponseToExaminationReportUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.managementResponseToExaminationReportUrl, 'Management Response To Examination Report')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(managementResponseToNDICExaminationReportsUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.managementResponseToNDICExaminationReportsUrl, 'Management Response To NDIC Examination Reports')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Regulatory Examination Reports -->

            <!-- Risk And Audit Structure -->
            <app-collapse-item
              title="Risk And Audit Structure"
              class="card-item px-2"
            >
              <validation-observer ref="riskAndAuditStructureRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Department Structure (Organogram) (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Department Structure (Organogram)"
                        rules="required"
                      >
                        <b-form-file
                          id="organogram-url"
                          v-model="dueDiligence.compulsoryDocument.riskAndAuditStructure.organogramUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.DEPARTMENT_STRUCTURE_ORGANOGRAM)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                        <small class="text-muted">Please upload the organizational chart illustrating the structure of the Risk Management and Internal Audit departments, including reporting lines.</small>
                      </validationprovider></b-col>
                  </b-row>

                  <b-row>
                    <b-col
                      md="12"
                      class="d-flex align-items-center justify-content-between mb-1 mt-0"
                    >
                      <span class="section-label">
                        Staff Information - Risk Management Unit
                      </span>
                      <Button
                        label="Add"
                        size="md"
                        icon="PlusIcon"
                        @click="addNewRiskManagementUnitStaffDetails"
                      />
                    </b-col>
                  </b-row>

                  <b-row
                    v-for="(item, index) in dueDiligence.compulsoryDocument.riskAndAuditStructure.riskManagementUnitStaffDetails.staffDetails"
                    :key="`risk-staff-${index}`"
                    class="match-height"
                  >
                    <b-col md="3">
                      <ValidationObserver>
                        <InputField
                          id="staff-full-name"
                          v-model="item.fullName"
                          label="Full Name"
                          type="text"
                          error-name="Full Name"
                          rules="required"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col md="3">
                      <ValidationObserver>
                        <InputField
                          id="designation"
                          v-model="item.designation"
                          label="Designation"
                          type="text"
                          error-name="Designation"
                          rules="required"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col md="3">
                      <ValidationObserver>
                        <SelectField
                          id="qualifications"
                          v-model="item.qualifications"
                          label="Qualifications"
                          multiple
                          :reduce="option => option.value"
                          :options="qualificationsOptions"
                          item="label"
                          placeholder="Select qualifications"
                          error-name="Qualifications"
                          rules="required"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col md="2">
                      <ValidationObserver>
                        <InputField
                          id="relevant-working-experience"
                          v-model="item.relevantWorkingExperience"
                          label="Relevant Working Experience"
                          type="text"
                          error-name="Relevant Working Experience"
                          rules="required"
                        />
                      </ValidationObserver>
                    </b-col>
                    <b-col
                      md="1"
                      class="mt-2"
                    >
                      <feather-icon
                        v-b-tooltip.hover.bottom="'Remove'"
                        icon="XIcon"
                        class="cursor-pointer text-danger"
                        @click="removeRiskManagementUnitStaffDetails(index)"
                      />
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Document (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Document"
                        rules="required"
                      >
                        <b-form-file
                          id="risk-management-unit-staff-info"
                          v-model="dueDiligence.compulsoryDocument.riskAndAuditStructure.riskManagementUnitStaffDetails.documentUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_UNIT_STAFF_DETAILS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col
                      md="12"
                      class="d-flex align-items-center justify-content-between mb-1 mt-0"
                    >
                      <span class="section-label">
                        Staff Information - Internal Audit Function
                      </span>
                      <Button
                        label="Add"
                        size="md"
                        icon="PlusIcon"
                        @click="addNewInternalAuditFunctionStaffDetails"
                      />
                    </b-col>
                  </b-row>

                  <b-row
                    v-for="(item, index) in dueDiligence.compulsoryDocument.riskAndAuditStructure.internalAuditFunctionStaffDetails.staffDetails"
                    :key="`internal-staff-${index}`"
                    class="match-height"
                  >
                    <b-col md="3">
                      <InputField
                        id="staff-full-name"
                        v-model="item.fullName"
                        label="Full Name"
                        type="text"
                        error-name="Full Name"
                        rules="required"
                      />
                    </b-col>
                    <b-col md="3">
                      <InputField
                        id="designation"
                        v-model="item.designation"
                        label="Designation"
                        type="text"
                        error-name="Designation"
                        rules="required"
                      />
                    </b-col>
                    <b-col md="3">
                      <SelectField
                        id="qualifications"
                        v-model="item.qualifications"
                        label="Qualifications"
                        multiple
                        :reduce="option => option.value"
                        :options="qualificationsOptions"
                        item="label"
                        placeholder="Select qualifications"
                        error-name="Qualifications"
                        rules="required"
                      />
                    </b-col>
                    <b-col md="2">
                      <InputField
                        id="relevant-working-experience"
                        v-model="item.relevantWorkingExperience"
                        label="Relevant Working Experience"
                        type="text"
                        error-name="Relevant Working Experience"
                        rules="required"
                      />
                    </b-col>
                    <b-col
                      md="1"
                      class="mt-2"
                    >
                      <feather-icon
                        v-b-tooltip.hover.bottom="'Remove'"
                        icon="XIcon"
                        class="cursor-pointer text-danger"
                        @click="removeInternalAuditFunctionStaffDetails(index)"
                      />
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Document (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Document"
                        rules="required"
                      >
                        <b-form-file
                          id="internal-audit-function-staff-info"
                          v-model="dueDiligence.compulsoryDocument.riskAndAuditStructure.internalAuditFunctionStaffDetails.documentUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_AUDIT_FUNCTION_STAFF_DETAILS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.riskAndAuditStructure.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveRiskAndAuditStructure"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="riskAndAuditStructureRef"
                :busy="isBusy"
                :items="riskAndAuditStructureList"
                responsive
                :fields="riskAndAuditStructureHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(organogramUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.organogramUrl, 'Organogram')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(riskManagementUnitStaffDetails)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.riskManagementUnitStaffDetails.documentUrl, 'Risk Management Unit Staff Details')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(internalAuditFunctionStaffDetails)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.internalAuditFunctionStaffDetails.documentUrl, 'Internal Audit Function Staff Details')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Risk And Audit Structure -->

            <!-- Ongoing Risk Monitoring -->
            <app-collapse-item
              title="Ongoing Risk Monitoring"
              class="card-item px-2"
            >
              <validation-observer ref="ongoingRiskMonitoringRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Risk Management Reports To Board (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Risk Management Reports To Board"
                        rules="required"
                      >
                        <b-form-file
                          id="risk-management-reports-board"
                          v-model="dueDiligence.compulsoryDocument.ongoingRiskMonitoring.riskManagementReportsToBoardUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_REPORTS_BOARD)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.ongoingRiskMonitoring.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveOngoingRiskMonitoring"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="ongoingRiskMonitoringRef"
                :busy="isBusy"
                :items="ongoingRiskMonitoringList"
                responsive
                :fields="ongoingRiskMonitoringHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(riskManagementReportsToBoardUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.riskManagementReportsToBoardUrl, 'Risk Management Reports To Board')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Ongoing Risk Monitoring -->

            <!-- Internal Audit Findings -->
            <app-collapse-item
              title="Internal Audit Findings"
              class="card-item px-2"
            >
              <validation-observer ref="internalAuditFindingsRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        {{ `Internal Audits Report ${lastYear} (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)` }}
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        :name="`Internal Audits Report ${lastYear}`"
                        rules="required"
                      >
                        <b-form-file
                          id="internal-audits-report_last-year"
                          v-model="dueDiligence.compulsoryDocument.internalAuditFindings.internalAuditsReportUrlLastYear"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_AUDIT_REPORTS_LAST_YEAR)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        {{ `Internal Audits Report ${lastTwoYears} (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)` }}
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        :name="`Internal Audits Report ${lastTwoYears}`"
                        rules="required"
                      >
                        <b-form-file
                          id="internal-audits-report_last_two_years"
                          v-model="dueDiligence.compulsoryDocument.internalAuditFindings.internalAuditsReportUrlTwoYearsAgo"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_AUDIT_REPORTS_LAST_TWO_YEARS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        {{ `Internal Audits Report ${lastThreeYears} (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)` }}
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        :name="`Internal Audits Report ${lastThreeYears}`"
                        rules="required"
                      >
                        <b-form-file
                          id="internal-audits-report_last_three_years"
                          v-model="dueDiligence.compulsoryDocument.internalAuditFindings.internalAuditsReportUrlThreeYearsAgo"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_AUDIT_REPORTS_LAST_THREE_YEARS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.internalAuditFindings.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveInternalAuditFindings"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="internalAuditFindingsRef"
                :busy="isBusy"
                :items="internalAuditFindingsList"
                responsive
                :fields="internalAuditFindingsHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(internalAuditsReportUrlLastYear)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.internalAuditsReportUrlLastYear, 'Internal Audits Report Last Year')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(internalAuditsReportUrlTwoYearsAgo)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.internalAuditsReportUrlTwoYearsAgo, 'Internal Audits Report Two Years Ago')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(internalAuditsReportUrlThreeYearsAgo)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.internalAuditsReportUrlThreeYearsAgo, 'Internal Audits Report Three Years Ago')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Internal Audit Findings -->

            <!-- Corporate Governance -->
            <app-collapse-item
              title="Corporate Governance"
              class="card-item px-2"
            >
              <validation-observer ref="corporateGovernanceRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Corporate Governance Policies & Code of Conduct (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Corporate Governance Policies & Code of Conduct"
                        rules="required"
                      >
                        <b-form-file
                          id="corporate-governance-policies-conduct"
                          v-model="dueDiligence.compulsoryDocument.corporateGovernance.corporateGovernanceUrl"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CORPRATE_GOVERNANCE_REPORT)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.corporateGovernance.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveCorporateGovernance"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="corporateGovernanceRef"
                :busy="isBusy"
                :items="corporateGovernanceList"
                responsive
                :fields="corporateGovernanceHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(corporateGovernanceUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.corporateGovernanceUrl, 'Corporate Governance')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Corporate Governance -->

            <!-- Collateral Management -->
            <!-- <app-collapse-item
              title="Collateral Management"
              class="card-item px-2"
            >
              <validation-observer ref="collateralManagementRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Collateral Valuation (Format accepted: PDF, DOC, DOCX)
                      </p>
                      <b-form-file
                        id="collateral-valuation"
                        v-model="dueDiligence.compulsoryDocument.collateralManagement.collateralValuationUrl"
                        label="Select File (.pdf, jpg, png format only)"
                        :state="Boolean(dueDiligence.compulsoryDocument.collateralManagement.collateralValuationUrl)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        error-name="Collateral Valuation"
                        rules="required"
                        accept=".pdf, .doc, .docx"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.COLLATERAL_VALUATION)"
                      />
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        National Collateral Registry (Format accepted: PDF, DOC, DOCX)
                      </p>
                      <b-form-file
                        id="national-collateral-register"
                        v-model="dueDiligence.compulsoryDocument.collateralManagement.nationalCollateralRegistryUrl"
                        label="Select File (.pdf, jpg, png format only)"
                        :state="Boolean(dueDiligence.compulsoryDocument.collateralManagement.nationalCollateralRegistryUrl)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        error-name=" National Collateral Registry"
                        rules="required"
                        accept=".pdf, .doc, .docx"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.NATIONAL_COLLATERAL_REGISTER)"
                      />
                    </b-col>
                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.collateralManagement.comment"
                        rows="2"
                        label="Comment"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveCollateralManagement"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="collateralManagementRef"
                :busy="isBusy"
                :items="collateralManagementList"
                responsive
                :fields="collateralManagementHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(collateralValuationUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.collateralValuationUrl, 'Collateral Valuation')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(nationalCollateralRegistryUrl)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.nationalCollateralRegistryUrl, 'National Collateral Registry')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(dateModified)="data">
                  {{ data.value ? toDateWithYear(data.value) : '' }}
                </template>
              </b-table>
            </app-collapse-item> -->
            <!-- Corporate Governance -->

            <!-- Credit Bureau Agreement -->

            <app-collapse-item
              title="Credit Bureau Agreement"
              class="card-item px-2"
            >
              <validation-observer ref="creditBureauAgreementRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Evidence of Agreement Credit Bureaus (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Evidence of Agreement Credit Bureaus"
                        rules="required"
                      >
                        <b-form-file
                          id="evidence-of-agreement-file-name"
                          v-model="dueDiligence.compulsoryDocument.creditBureauAgreement.evidenceOfAgreement"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          multiple
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.EVIDENCE_OF_AGREEMENT)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.compulsoryDocument.creditBureauAgreement.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>
                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveCreditBureau"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="creditBureauRef"
                :busy="isBusy"
                :items="creditBureauList"
                responsive
                :fields="creditBureauHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(evidenceOfAgreement)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.evidenceOfAgreement, 'Evidence Of Agreement')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>

            <!-- Credit Bureau Agreement -->
          </app-collapse>
        </b-tab>

        <b-tab title="Supporting Documents">
          <app-collapse
            class="business-items"
            type="margin"
            accordion
          >
            <!-- Customer Complaints Handling -->
            <app-collapse-item
              title="Customer Complaints Handling"
              class="card-item px-2"
            >
              <validation-observer ref="customerComplaintsHandlingRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Customer Complaint Management System/Framework Bureaus(Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Customer Complaint Management System/Framework Bureaus"
                        rules="required"
                      >
                        <b-form-file
                          id="file-name"
                          v-model="dueDiligence.supportingDocument.customerComplaintsHandling.customerComplaintsManagement"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          multiple
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CUSTOMER_COMPLAINTS_MANAGEMENT)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                      <small class="text-muted">Please upload the document outlining your bank's Customer Complaints Management System and Framework. This may include policies, procedures, and process flow diagram</small>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.customerComplaintsHandling.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>
                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveCustomerComplaintsHandling"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="customerComplaintHandlingRef"
                :busy="isBusy"
                :items="customerComplaintHandlingList"
                responsive
                :fields="customerComplaintHandlingHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(customerComplaintManagement)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.customerComplaintManagement, 'Customer Complaint Management')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <!-- Customer Complaints Handling -->

            <app-collapse-item
              title="Consumer Credit Programs"
              class="card-item px-2"
            >
              <validation-observer ref="consumerCreditRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Copies of Consumer Credit Product Programs (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Copies of Consumer Credit Product Programs"
                        rules="required"
                      >

                        <b-form-file
                          id="copies-of-consumer-file-name"
                          v-model="dueDiligence.supportingDocument.consumerCreditProgram.copiesConsumerCredit"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          multiple
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.COPIES_CONSUMER_CREDIT)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.consumerCreditProgram.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>
                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveConsumerCreditProgram"
                    />
                  </div>
                </b-form>
              </validation-observer>
              <b-table
                ref="consumerCreditRef"
                :busy="isBusy"
                :items="consumerCreditList"
                responsive
                :fields="consumerCreditHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(copiesConsumerCredit)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.copiesConsumerCredit, 'Copies Consumer Credit')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <app-collapse-item
              title="Consumer Loan Pricing"
              class="card-item px-2"
            >
              <validation-observer ref="consumerLoanPricingRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Loan Pricing Strategy for Consumer Credit Customers (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Loan Pricing Strategy for Consumer Credit Customers"
                        rules="required"
                      >

                        <b-form-file
                          id="loan-pricing-strategy-file-name"
                          v-model="dueDiligence.supportingDocument.consumerLoanPricing.loanPricingStrategy"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.LOAN_PRICING_STRATEGY)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.consumerLoanPricing.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>
                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveConsumerLoanPricing"
                    />
                  </div>
                </b-form>
              </validation-observer>
              <b-table
                ref="consumerLoanPricingRef"
                :busy="isBusy"
                :items="consumerLoanPricingList"
                responsive
                :fields="consumerLoanPricingHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(loanPricingStrategy)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.loanPricingStrategy, 'Loan Pricing Strategy')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <app-collapse-item
              title="External Board Assessment"
              class="card-item px-2"
            >
              <validation-observer ref="externalBoardAssessmentRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Most recent External Board Evaluation/Assessment Report (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Most Recent External Board Evaluation/Assessment Report"
                        rules="required"
                      >
                        <b-form-file
                          id="external-board-evaluation-file-name"
                          v-model="dueDiligence.supportingDocument.externalBoardAssessment.recentExternalBoard"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          multiple
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RECENT_EXTERNAL_BOARD)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.externalBoardAssessment.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>
                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveExternalBoardAssessment"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="externalBoardAssessmentRef"
                :busy="isBusy"
                :items="externalBoardAssessmentList"
                responsive
                :fields="externalBoardAssessmentHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(mostRecentExternalBoardEvaluation)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.mostRecentExternalBoardEvaluation, 'Most Recent External Board Evaluation')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <app-collapse-item
              title="IT System Agreements"
              class="card-item px-2"
            >
              <validation-observer ref="itSystemAgreementsRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Signed Agreement with Banking Application and Other IT Vendor (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Signed Agreement with Banking Application and Other IT Vendor"
                        rules="required"
                      >
                        <b-form-file
                          id="signed-agreement-file-name"
                          v-model="dueDiligence.supportingDocument.itSystemAgreements.signedAgreement"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          multiple
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.SIGNED_AGREEMENT)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.itSystemAgreements.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveITSystemAgreement"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="itSystemAgreementsRef"
                :busy="isBusy"
                :items="itSystemAgreementsList"
                responsive
                :fields="itSystemAgreementsHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(signedAgreement)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.signedAgreement, 'Signed Agreement')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
            <app-collapse-item
              title="Tax Deduction Remittances"
              class="card-item px-2"
            >
              <validation-observer ref="taxDeductionRemittancesRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Evidence of Tax (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Evidence of Tax"
                        rules="required"
                      >
                        <b-form-file
                          id="tax-deduction-remittances-file-name"
                          v-model="dueDiligence.supportingDocument.taxDeductionRemittances.taxRemittance"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.EVIDENCE_OF_TAX)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Evidence of Pension (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Evidence of Pension"
                        rules="required"
                      >
                        <b-form-file
                          id="evidence-of-pension-file-name"
                          v-model="dueDiligence.supportingDocument.taxDeductionRemittances.pensionNHFRemittance"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.EVIDENCE_OF_PENSION)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Evidence of Other Deduction Remittances (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)

                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Evidence of Other Deduction Remittances"
                        rules="required"
                      >
                        <b-form-file
                          id="other-deduction-remittances-file-name"
                          v-model="dueDiligence.supportingDocument.taxDeductionRemittances.otherDeductionRemittances"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.EVIDENCE_OF_OTHER_REMITTANCES)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.taxDeductionRemittances.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveTaxDeductionRemittances"
                    />
                  </div>
                </b-form>
              </validation-observer>
              <b-table
                ref="taxDeductionRef"
                :busy="isBusy"
                :items="taxDeductionList"
                responsive
                :fields="taxDeductionHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(evidenceOfTaxRemittances)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.evidenceOfTaxRemittances, 'Evidence Of Tax Remittances')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(evidenceOfPension)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.evidenceOfPension, 'Evidence Of Pension')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(evidenceOfOtherDeductions)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.evidenceOfOtherDeductions, 'Evidence Of OtherDeductions')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>

            <app-collapse-item
              title="Environment and Social Risk"
              class="card-item px-2"
            >
              <validation-observer ref="environmentAndSocialRiskRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Environmental And Social RiskManagement Policies (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Environmental And Social RiskManagement Policies"
                        rules="required"
                      >
                        <b-form-file
                          id="environmental-and-social-risk-management-policies-file-name"
                          v-model="dueDiligence.supportingDocument.environmentAndSocialRisk.environmentalAndSocialRiskManagementPolicies"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.ENVIRONMENTAL_AND_SOCIAL_RISK)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Relevant Policies Framework (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Relevant Policies Framework"
                        rules="required"
                      >

                        <b-form-file
                          id="relevant-policies-framework-file-name"
                          v-model="dueDiligence.supportingDocument.environmentAndSocialRisk.relevantPoliciesFramework"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RELEVANT_POLICIES_FRAMEWORK)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Implementation Evidence (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Implementation Evidence"
                        rules="required"
                      >
                        <b-form-file
                          id="implementation-evidence-file-name"
                          v-model="dueDiligence.supportingDocument.environmentAndSocialRisk.implementationEvidence"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.IMPLEMENTATION_EVIDENCE)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.environmentAndSocialRisk.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveEnvironmentAndSocialRisk"
                    />
                  </div>
                </b-form>
              </validation-observer>
              <b-table
                ref="environmentAndSocialRiskRef"
                :busy="isBusy"
                :items="environmentAndSocialRiskList"
                responsive
                :fields="environmentAndSocialRiskHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(environmentalAndSocialRiskManagementPolicies)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.environmentalAndSocialRiskManagementPolicies, 'Environmental And Social Risk Management Policies')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(relevantPoliciesFramework)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.relevantPoliciesFramework, 'Relevant Policies Framework')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(implementationEvidence)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.implementationEvidence, 'Implementation Evidence')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>

            <app-collapse-item
              title="Advanced Risk Analysis"
              class="card-item px-2"
            >

              <validation-observer ref="advancedRiskAnalysisRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Stress Test Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Stress Test Reports"
                        rules="required"
                      >
                        <b-form-file
                          id="stress-test-reports-file-name"
                          v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.stressTestReports"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.STRESS_TEST_REPORTS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Market Sensitivity Analysis Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Market Sensitivity Analysis Reports"
                        rules="required"
                      >
                        <b-form-file
                          id="market-sensitivity-analysis-reports-file-name"
                          v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.marketSensitivityAnalysisReports"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.MARKET_SENSITIVITY_ANALYSIS_REPORTS)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Liquidity Contingency Funding Plan (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Liquidity Contingency Funding Plan"
                        rules="required"
                      >
                        <b-form-file
                          id="liquidity-contingency-funding-plan-file-name"
                          v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.liquidityContingencyFundingPlan"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.LIQUIDITY_CONTINGENCY_FUNDING_PLAN)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Business Continuity And Disaster Recovery Plan (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Business Continuity And Disaster Recovery Plan"
                        rules="required"
                      >
                        <b-form-file
                          id="business-continuity-and-disaster-recovery-plan-file-name"
                          v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.BusinessContinuityAndDisasterRecoveryPlan"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.BUSINESS_CONTINUITY_AND_DISASTER_RECOVERY_PLAN)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>

                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="12">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveAdvancedRiskAnalysis"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="advancedRiskAnalysisRef"
                :busy="isBusy"
                :items="advancedRiskAnalysisList"
                responsive
                :fields="advancedRiskAnalysisHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >

                <template #cell(stressTestReports)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.stressTestReports, 'Environmental And Social Risk Management Policies')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(marketSensitivityAnalysisReports)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.marketSensitivityAnalysisReports, 'Relevant Policies Framework')"
                  >
                    View Document
                  </div>
                </template>
                <template #cell(liquidityContingencyFundingPlan)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.liquidityContingencyFundingPlan, 'Implementation Evidence')"
                  >
                    View Document
                  </div>
                </template>

                <template #cell(businessContinuityAndDisasterRecoveryPlan)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.businessContinuityAndDisasterRecoveryPlan, 'Implementation Evidence')"
                  >
                    View Document
                  </div>
                </template>

              </b-table>
            </app-collapse-item>

            <app-collapse-item
              title="Rating Agency Reports"
              class="card-item px-2"
            >

              <validation-observer ref="ratingAgencyReportsRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Reports of Rating Agencies (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Reports of Rating Agencies"
                        rules="required"
                      >
                        <b-form-file
                          id="reports-of-rating-agencies-file-name"
                          v-model="dueDiligence.supportingDocument.ratingAgencyReports.ratingAgencyReport"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RATING_AGENCY_REPORT)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.ratingAgencyReports.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveRatingAgencyReports"
                    />
                  </div>
                </b-form>
              </validation-observer>

              <b-table
                ref="ratingAgencyReportsRef"
                :busy="isBusy"
                :items="ratingAgencyReportsList"
                responsive
                :fields="ratingAgencyReportsHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(ratingAgencyReport)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.ratingAgencyReport, 'Rating Agency Report')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>

            <app-collapse-item
              title="Internal Risk Monitoring"
              class="card-item px-2"
            >
              <validation-observer ref="internalRiskMonitoringRules">
                <b-form>
                  <b-row class="match-height">
                    <b-col
                      md="6"
                      class="mb-2"
                    >
                      <p class="upload-label">
                        Risk Registers and ERM Risk Dashboard (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                      </p>
                      <ValidationProvider
                        v-slot="{ errors, valid }"
                        name="Risk Registers and ERM Risk Dashboard"
                        rules="required"
                      >

                        <b-form-file
                          id="risk-registers-file-name"
                          v-model="dueDiligence.supportingDocument.internalRiskMonitoring.riskRegisters"
                          label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                          :state="errors.length > 0 ? false : (valid ? true : null)"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                          accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                          multiple
                          @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_RISK_MONITORING)"
                        />
                        <b-form-invalid-feedback>
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                  </b-row>
                  <b-row class="match-height">
                    <b-col md="6">
                      <TextAreaField
                        id="comment"
                        v-model="dueDiligence.supportingDocument.internalRiskMonitoring.comment"
                        rows="2"
                        label="Comment (Optional)"
                        placeholder="Enter Comment"
                        type="text"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>

                  <div class="w-100 d-flex justify-content-end">
                    <Button
                      label="Save"
                      size="md"
                      @click="saveInternalRiskMonitoring"
                    />
                  </div>
                </b-form>
              </validation-observer>
              <b-table
                ref="internalRiskMonitoringRef"
                :busy="isBusy"
                :items="internalRiskMonitoringList"
                responsive
                :fields="internalRiskMonitoringHeaders"
                primary-key="id"
                show-empty
                empty-text="No records available"
                class="position-relative mt-4"
              >
                <template #cell(internalRiskMonitoring)="data">
                  <div
                    class="text-decoration-underline cursor-pointer text-primary"
                    @click="onViewDocumentFile(data.item.internalRiskMonitoring, 'Internal Risk Monitoring')"
                  >
                    View Document
                  </div>
                </template>
              </b-table>
            </app-collapse-item>
          </app-collapse>
        </b-tab>
      </b-tabs>
      <FullScreenSpinner :show="showSpinnerDoc" />

    </b-modal>
    <!-- due diligence modal -->

    <!-- Risk Management Unit Staff Details -->
    <b-modal
      id="risk-management-unit-staff-details-modal"
      ref="risk-management-unit-staff-details"
      ok-only
      no-close-on-backdrop
      centered
      size="lg"
      title="Risk Management Unit Staff Details"
      modal-footer
    >

      <b-table
        ref="risk-management-staff-Ref"
        :busy="isBusy"
        :per-page="pageSize"
        :current-page="currentPage"
        :items="riskManagementStaffInformationList"
        responsive
        :fields="riskManagementStaffInformationHeaders"
        primary-key="id"
        show-empty
        empty-text="No records available"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>
        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>
        <template #cell(documentUrl)="data">
          <b-link
            :href="data.value"
            target="_blank"
          >
            View Document
          </b-link>
        </template>
      </b-table>
    </b-modal>
    <!-- Risk Management Unit Staff Details -->

    <!-- Internal Audit Function Staff Details -->
    <b-modal
      id="internal-audit-unit-staff-details-modal"
      ref="internal-audit-unit-staff-details"
      ok-only
      no-close-on-backdrop
      centered
      size="lg"
      title="Internal Audit Function Staff Details"
      modal-footer
    >

      <b-table
        ref="internal-audit-staff-Ref"
        :busy="isBusy"
        :per-page="pageSize"
        :current-page="currentPage"
        :items="internalAuditFunctionStaffInformationList"
        responsive
        :fields="internalAuditFunctionStaffInformationHeaders"
        primary-key="id"
        show-empty
        empty-text="No records available"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>
        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>
        <template #cell(documentUrl)="data">
          <b-link
            :href="data.value"
            target="_blank"
          >
            View Document
          </b-link>
        </template>
      </b-table>
    </b-modal>
    <!-- Internal Audit Function Staff Details -->

    <!-- Credit Limit -->
    <b-modal
      id="credit-limit-modal"
      ref="creditLimitComponent"
      :title="modalCreateLimitTitle"
      ok-title="Cancel"
      ok-only
      ok-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      @hidden="resetCreditLimitFilter"
    >
      <CreditLimit
        :credit-limit-data="creditLimitFormData"
        :is-readonly="isReadonly"
        :button-label="creditLimitButton"
        :credit-limit-items="creditLimitList"
        :is-table-busy="isCreditLimitBusy"
        :per-page="pageSize"
        :current-page-prop="currentPage"
        @credit-limit-action="handleCreditLimit"
      />
    </b-modal>
    <!-- Credit Limit -->

    <!-- Master Finance Agreement -->
    <b-modal
      id="master-financing-agreement-modal"
      ref="masterFinancingAgreementModal"
      :title="modalMFATitle"
      :ok-title="mfaButton"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      hide-footer
      @hidden="resetMFAFormModal"
    >
      <validation-observer ref="mfaRules">
        <b-form>
          <!-- Download MFA -->
          <b-row>
            <b-col
              md="6"
              class="mt-2"
            >
              <InputField
                id="master-financing-agreement"
                v-model="masterFinancingAgreement"
                label=""
                type="text"
                readonly
              />
            </b-col>
            <b-col
              md="6"
              class=" mt-3"
            >
              <Button
                label="Download Master Financing Agreement"
                size="md"
                icon="DownloadIcon"
                show-icon
                class="mb-1"
                @click="downloadMFA()"
              />
            </b-col>
          </b-row>
          <!-- Download MFA -->
          <hr class="horizontal-line">
          <h6 class="section-label mb-1 mt-4 ml-5">
            Master Financing Agreement Document Upload
          </h6>
          <b-row class="match-height mb-2 mt-1 ml-5">
            <b-col
              md="6"
              class="upload-image"
            >
              <Spinner :show="!!documentData.loading" />

              <validation-provider
                v-slot="{ errors }"
                :name="`${documentData.title || 'Document File'}`"
                :rules="fileRules"
              >
                <b-form-group
                  :state="!errors.length"
                  :invalid-feedback="errors[0] || documentData.validationError"
                >
                  <b-form-file
                    v-model="documentData.file"
                    :state="!errors.length && !documentData.validationError"
                    placeholder="Choose a file or drop it here..."
                    drop-placeholder="Drop file here..."
                    accept=".jpg,.jpeg,.png, .docx,.pdf, application/pdf, .xlsx, .csv"
                    plain
                    :disabled="isReadonly"
                    @change="handleFileUpload($event)"
                  />

                  <!-- Instructions -->
                  <div
                    v-if="!documentData.url"
                    class="upload-instruction text-center"
                  >
                    <h4>{{ `${documentData.title || 'Upload Document File'}` }}</h4>
                    <p>Drag & drop file or choose from your device to upload</p>
                    <p class="format">
                      Format accepted: JPG, PNG, JPEG, DOCX, PDF, XLSX, CSV
                    </p>
                  </div>

                  <!-- Preview -->
                  <div v-else>
                    <b-img
                      v-if="['png','jpeg','jpg','bmp'].includes(documentData.fileExtension)"
                      :src="documentData.url"
                      block
                      rounded
                      width="250"
                      height="250"
                    />
                    <b-img
                      v-else-if="documentData.fileExtension === 'pdf'"
                      :src="pdfIcon"
                      block
                      rounded
                      width="250"
                      height="250"
                    />
                    <b-img
                      v-else-if="documentData.fileExtension === 'csv'"
                      :src="csvIcon"
                      block
                      rounded
                      width="250"
                      height="250"
                    />
                    <b-img
                      v-else-if="documentData.fileExtension === 'xlsx'"
                      :src="excelIcon"
                      block
                      rounded
                      width="250"
                      height="250"
                    />
                    <b-img
                      v-else-if="documentData.fileExtension === 'docx'"
                      :src="wordIcon"
                      block
                      rounded
                      width="250"
                      height="250"
                    />
                    <b-img
                      v-else
                      src=""
                    />

                    <div class="view-details mt-1">
                      <feather-icon
                        icon="EyeIcon"
                        size="20"
                        class="text-primary"
                        @click="onViewDocumentFile(documentData.url, documentData.title)"
                      />
                    </div>
                  </div>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="3"
              class="mt-0"
            >
              <b-row>
                <!-- Remove Button -->
                <b-col
                  v-if="documentData.url"
                  md="12"
                  class="mt-1"
                >
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="ml-0"
                    @click="removeDocument(documentData.documentId)"
                  >
                    <feather-icon
                      icon="XIcon"
                      class="mr-25"
                    />
                    <span>Remove</span>
                  </b-button>
                </b-col>
                <b-col
                  v-if="documentData.url"
                  md="12"
                  class="mt-1"
                >
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="ml-0"
                    @click="handleDownload(documentData.url)"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      class="mr-25"
                    />
                    <span>Download</span>
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <div class="w-100 d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="$refs.masterFinancingAgreementModal.hide()"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              @click="handleMFASubmit"
            >
              Submit
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- Master Finance Agreement -->
    <b-modal
      id="view-document-modal"
      ref="avatarModal"
      no-close-on-backdrop
      :ok-title="$t('Download File')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="viewDocumentModalTitle"
      @hidden="resetDocumentModal"
      @ok="handleDownload(viewDocumentData)"
    >
      <DocumentViewer
        :document-url="viewDocumentData"
        :loading="showSpinnerDoc"
        @loaded="onDocumentLoaded"
        @error="onDocumentError"
      />
    </b-modal>
    <!-- Display Validation Errors -->
    <b-modal
      id="all-validated-modal"
      ref="allValidatedModal"
      ok-title="Cancel"
      ok-only
      no-close-on-backdrop
      ok-variant="outline-secondary"
      centered
      size="md"
      title="Kindly attend to the field(s) below"
      @hidden="resetValidationModal"
    >
      <div
        v-for="(errorItem, index) in allValidationErrors"
        :key="index"
      >
        <p>{{ `${index + 1}.` }} <span style="color: red;"> {{ errorItem }}</span></p>
      </div>
    </b-modal>
    <!-- Display Validation Errors -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
/* eslint-disable prefer-destructuring */
import {
  BCard, BRow, BCol, BTable, BBadge, BForm, BButton, BTabs, BTab,
  BFormFile, BDropdown, BDropdownItem, BSpinner, BFormGroup, BImg,
  VBTooltip, BLink, BFormInvalidFeedback,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import Ripple from 'vue-ripple-directive'
import * as FileSaver from 'file-saver'
import { v4 as uuidv4 } from 'uuid'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import { heightFade } from '@core/directives/animations'
import { toastAlert } from '@core/mixins/toast'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import Spinner from '@/views/components/spinner/Spinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import SelectField from '../../../forms/SelectField.vue'
import TextAreaField from '../../../forms/TextAreaField.vue'
import { toDateWithYear, calculateDurationString } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import { lmsPermissions, accountPermissions } from '../../../../helpers/permissions'
import addSpaceToString from '../../../../helpers/addSpaceToString'
import { toBalance, toKobo } from '../../../../helpers/formatAmount'
import {
  titleOptions, maritalStatusOptions, gender, countries, meansOfIdOptions,
  pdfIcon, wordIcon, excelIcon, csvIcon,
} from '@/helpers/data'
import {
  DUE_DILIGENCE_ENUMS, DUE_DILIGENCE_UPLOAD_TYPES, processingConfig, saveConfig, documentPathMap,
} from '@/helpers/constants'
import { checkDueDiligenceCompleteness, addSpacesToLabel } from '@/helpers/misc'
import DocumentViewer from '@/views/components/DocumentViewer.vue'
import CreditLimit from '../../../forms/reusables/CreditLimit.vue'
// import KYCInformation from '../../../forms/reusables/KYCInformation.vue'

const base64Encode = data => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.readAsDataURL(data)
  reader.onload = () => resolve(reader.result)
  reader.onerror = error => reject(error)
})
const currentYear = new Date().getFullYear()
const lastThreeYears = currentYear - 3
const lastTwoYears = currentYear - 2
const lastYear = currentYear - 1

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTabs,
    BTab,
    BTable,
    BForm,
    BButton,
    BLink,
    ValidationObserver,
    ValidationProvider,
    BImg,
    BFormGroup,
    BFormFile,
    BDropdown,
    BDropdownItem,
    BBadge,
    BSpinner,
    InputField,
    InputNumberField,
    SelectField,
    TextAreaField,
    TablePagination,
    FullScreenSpinner,
    Spinner,
    Button,
    DatePickerField,
    TableEntries,
    AppCollapse,
    AppCollapseItem,
    CreditLimit,
    DocumentViewer,
    BFormInvalidFeedback,
    // KYCInformation,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin, toastAlert],
  props: {
    initialLogoSrc: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      lastYear,
      lastTwoYears,
      lastThreeYears,
      pdfIcon,
      wordIcon,
      excelIcon,
      csvIcon,
      titleOptions,
      meansOfIdOptions,
      maritalStatusOptions,
      genderOptions: gender,
      toBalance,
      addSpaceToString,
      toDateWithYear,
      sortBy: 'name',
      isBusy: false,
      isKycBusy: false,
      showSpinner: false,
      showSpinnerDoc: false,
      isKycLoading: false,
      isReadonly: false,
      required,
      getStatusColor,
      finalPayload: [],
      modalTitle: 'Create PFI Onboarding',
      isLoading: false,
      isDisabled: false,
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'partnerCode', sortable: true, label: 'Partner Code' },
        { key: 'institutionName', sortable: true, label: 'Institution Name' },
        { key: 'institutionEmail', sortable: true, label: 'Institution Email' },
        { key: 'institutionAddress', sortable: true, label: 'Institution Address' },
        { key: 'fullName', sortable: true, label: 'Contact Fullname' },
        { key: 'emailAddress', sortable: true, label: 'Contact Email Address' },
        { key: 'contactPhoneNumber', sortable: true, label: 'Contact Phone Number' },
        { key: 'roleWithinTheOrganization', sortable: true, label: 'Role Within The Organization' },
        { key: 'city', sortable: true, label: 'Institution City' },
        { key: 'state', sortable: true, label: 'Institution State' },
        { key: 'localGovernmentArea', sortable: true, label: 'Institution LGA' },
        { key: 'shareholdersFund', sortable: true, label: 'Shareholder Fund' },
        { key: 'financialInstitutionType', sortable: true, label: 'Institution Type' },
        { key: 'operatingLicenseIssueDate', sortable: true, label: 'License Issue Date' },
        { key: 'operationCommencementDate', sortable: true, label: 'License Commencement Date' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions' },
      ],
      DUE_DILIGENCE_UPLOAD_TYPES,
      pfiOnboardingList: [],
      filter: {
        institutionName: '',
        email: '',
        partnerCode: '',
      },
      uploadSupportingDocListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'description', sortable: true, label: 'Document Description' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'comment', label: 'Comment' },
        { key: 'dateLastModified', label: 'Upload Date' },
        { key: 'uploadedBy', label: 'Uploaded By' },
        { key: 'approvalStatus', label: 'Approval Status' },
        { key: 'actions', label: 'Actions' },
      ],
      uploadSupportingDocList: [],
      supportingDocumentType: '',
      uploadDocumentsData: {},
      documentIdentifier: '',
      kycDocumentIdentifier: '',
      documentImage: null,
      documentImageSrc: this.initialLogoSrc || '',
      uploadLoadSpinner: false,

      kycDocumentImage: null,
      kycDocumentImageSrc: this.initialLogoSrc || '',
      uploadKycLoadSpinner: false,
      directorButtonLabel: 'Add Director',
      bankButtonLabel: 'Save Bank Information',
      imageFormData: {
        cbnAuditAccount: '',
      },
      kycInformationHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'title', sortable: true, label: 'Title' },
        { key: 'firstName', label: 'Firstname' },
        { key: 'lastName', sortable: true, label: 'Last Name' },
        { key: 'phoneNumber', label: 'Phone Number' },
        { key: 'pep', label: 'PEP' },
        { key: 'status', label: 'Status' },
        { key: 'bvn', label: 'BVN' },
        { key: 'actions', label: 'Actions' },
      ],
      kycInformationList: [],
      kycInformationFormData: {
        lastName: '',
        firstName: '',
        middleName: '',
        title: '',
        phoneNumber: '',
        emailAddress: '',
        bvn: '',
        pep: false,
        meansOfIdentification: '',
        idNumber: '',
        meansOfIdentificationIssueDate: null,
        meansOfIdentificationExpiryDate: null,
        meansOfIdentificationPlaceOfIssue: '',
        // documentIdentifier: '',
        id: null,
      },
      kycDirectorFile: {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
        validIDStatus: '',
      },
      kycBankFormData: {
        tin: '',
        bankName: '',
        institutionEmail: '',
        institutionAddress: '',
      },
      nonDisclosureAgreementFile: {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      },
      directDebitMandateFile: {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      },
      antiMoneyLaunderingQuestionnaireFile: {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      },
      declarationOfUltimateBeneficialOwnershipFile: {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      },
      signatureMandateFile: {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      },
      boardResolutionFile: {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      },
      actionType: 'add',
      isKycReadonly: false,
      dataItems: '',
      lgaOptions: [],
      countryOptions: ['Nigeria'],
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        ariaDateFormat: 'Y-m-d',
      },
      meanOfIDdatePickerConfig: {
        dateFormat: 'Y-m-d',
        // minDate: 'today',
        ariaDateFormat: 'Y-m-d',
      },
      dueDiligenceDatePickerConfig: {
        dateFormat: 'd-m-Y',
        maxDate: 'today',
        ariaDateFormat: 'd-m-Y',
      },
      dateOfBirthDatePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        ariaDateFormat: 'Y-m-d',
      },
      employStatusOption: [
        { value: 'Employed', label: 'Employed' },
        { value: 'Unemployed', label: 'Unemployed' },
      ],
      pepOptions: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      currentIndex: null,
      currentValue: '',
      viewDocumentData: '',
      viewDocumentModalTitle: '',
      fileExtension: '',
      isSubmittingDueDiligence: false,
      qualificationsOptions: [
        { value: '1', label: 'BSc' },
        { value: '2', label: 'MSc' },
        { value: '3', label: 'PhD' },
        { value: '4', label: 'MBA' },
        { value: '5', label: 'CFA' },
        { value: '6', label: 'ACCA' },
        { value: '7', label: 'CPA' },
        { value: '8', label: 'CISA' },
        { value: '9', label: 'CISSP' },
        { value: '10', label: 'Other' },
      ],
      riskManagementStaffInformationList: [],
      riskManagementStaffInformationHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'designation', sortable: true, label: 'Designation' },
        { key: 'qualifications', label: 'Qualifications' },
        { key: 'relevantWorkingExperience', label: 'Relevant Working Experience' },
        { key: 'documentUrl', label: 'Document' },
      ],
      internalAuditFunctionStaffInformationList: [],
      internalAuditFunctionStaffInformationHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'designation', sortable: true, label: 'Designation' },
        { key: 'qualifications', label: 'Qualifications' },
        { key: 'relevantWorkingExperience', label: 'Relevant Working Experience' },
        { key: 'documentUrl', label: 'Document' },
      ],

      dueDiligence: {
        compulsoryDocument: {
          legalRegDocuments: {
            cacRegistration: [],
            articleOfAssociation: [],
            operatingLicense: [],
            comment: '',
          },
          boardManagement: {
            designation: '',
            duration: '',
            // fullName: '',
            lastName: '',
            firstName: '',
            middleName: '',
            cvUpload: [],
            yearAppointed: '',
            cbnApprovalEvidence: [],
            comment: '',
          },
          leadershipGovernance: {
            bankOrganisationStructure: [],
            bankOrganisationChart: [],
            comment: '',
          },
          financialStatements: {
            auditedFinancialStatement1: [],
            auditedFinancialStatement2: [],
            auditedFinancialStatement3: [],
            comment: '',
          },
          recentFinancialPerformance: {
            latestFinancialPerformance: [],
            capitalAdequacyRatio: '',
            nonPerformingLoanRatio: '',
            liquidityRatio: '',
            loanToDepositRatio: '',
            comment: '',
          },
          creditRiskManagement: {
            creditRiskStrategyAndAnnualPortfolioPlan: [],
            comment: '',
          },
          riskManagementFramework: {
            riskManagementFrameworkUrl: [],
            policiesUrl: [],
            limitsUrl: [],
            manualUrl: [],
            comment: '',
          },
          loanPortfolioDetails: {
            creditPortfolioReport: [],
            comment: '',
          },
          aMLCFTCompliance: {
            antiMoneyLaundryUrl: [],
            counterTerroristFinancingPoliciesUrl: [],
            comment: '',
          },
          regulatoryCompliance: {
            regulatoryReturnsRenditionUrl: [],
            comment: '',
          },
          regulatoryExaminationReports: {
            cBNExaminationReportsUrl: [],
            nDICExaminationReportUrl: [],
            managementResponseToExaminationReportUrl: [],
            managementResponseToNDICExaminationReportsUrl: [],
            comment: '',
          },
          riskAndAuditStructure: {
            organogramUrl: [],
            riskManagementUnitStaffDetails: {
              staffDetails: [{
                fullName: '',
                designation: '',
                qualifications: [],
                relevantWorkingExperience: '',
              }],
              documentUrl: [],
            },
            internalAuditFunctionStaffDetails: {
              staffDetails: [{
                fullName: '',
                designation: '',
                qualifications: [],
                relevantWorkingExperience: '',
              }],
              documentUrl: [],

            },
            comment: '',
          },
          ongoingRiskMonitoring: {
            riskManagementReportsToBoardUrl: [],
            comment: '',
          },
          internalAuditFindings: {
            internalAuditsReportUrlLastYear: [],
            internalAuditsReportUrlTwoYearsAgo: [],
            internalAuditsReportUrlThreeYearsAgo: [],
            comment: '',
          },
          corporateGovernance: {
            corporateGovernanceUrl: [],
            comment: '',
          },
          collateralManagement: {
            collateralValuationUrl: [],
            nationalCollateralRegistryUrl: [],
            comment: '',
          },
          creditBureauAgreement: {
            evidenceOfAgreement: [],
            comment: '',
          },
        },
        // optionalDocuments: {
        //   creditBureauAgreement: {
        //     evidenceOfAgreement: [],
        //     comment: '',
        //   },
        //   customerComplaintsHandling: {
        //     customerComplaintsManagement: [],
        //     comment: '',
        //   },
        // },
        supportingDocument: {
          customerComplaintsHandling: {
            customerComplaintsManagement: [],
            comment: '',
          },
          consumerCreditProgram: {
            copiesConsumerCredit: [],
            comment: '',
          },
          consumerLoanPricing: {
            loanPricingStrategy: [],
            comment: '',
          },
          externalBoardAssessment: {
            recentExternalBoard: [],
            comment: '',
          },
          itSystemAgreements: {
            signedAgreement: [],
            comment: '',
          },
          taxDeductionRemittances: {
            taxRemittance: [],
            pensionNHFRemittance: [],
            otherDeductionRemittances: [],
            comment: '',
          },
          ratingAgencyReports: {
            ratingAgencyReport: [],
            comment: '',
          },
          internalRiskMonitoring: {
            ratingAgencyReport: [],
            riskRegisters: '',
          },
          environmentAndSocialRisk: {
            environmentalAndSocialRiskManagementPolicies: [],
            relevantPoliciesFramework: [],
            implementationEvidence: [],
            comment: '',
          },
          advancedRiskAnalysis: {
            stressTestReports: [],
            marketSensitivityAnalysisReports: [],
            liquidityContingencyFundingPlan: [],
            businessContinuityAndDisasterRecoveryPlan: [],
            comment: '',
          },
        },
      },
      legalDocumentList: [],
      legalDocumentHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'evidenceOfCACRegistration', sortable: true },
        { key: 'memorandum', sortable: true, label: 'Memorandum & Article of Association' },
        { key: 'operatingLicense', sortable: true, label: 'Operating License of CBN ' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      boardManagementList: [],
      boardManagementHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'lastName', sortable: true },
        { key: 'firstName', sortable: true },
        { key: 'middleName', sortable: true },
        { key: 'designation', sortable: true },
        { key: 'cvUpload', sortable: true, label: 'Profile/CV Upload' },
        { key: 'cbnApprovalEvidence', sortable: true, label: 'CBN Approval Evidence' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'dateAppointed', sortable: true, label: 'Year Appointed' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      leadershipAndGovernanceList: [],
      leadershipAndGovernanceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'bankOrganistionStructure', sortable: true },
        { key: 'banksOrganisationalChart', sortable: true, label: "Bank's Organisational Chart" },
        { key: 'comment', sortable: true },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      financialStatementList: [],
      financialStatementHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'auditedFinancialStatement1', sortable: true },
        { key: 'auditedFinancialStatement2', sortable: true },
        { key: 'auditedFinancialStatement3', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      recentFinancialPerformanceList: [],
      recentFinancialPerformanceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'latestFinancialPerformance', sortable: true, label: 'Latest Quarterly Management Accounts' },
        { key: 'capitalAdequacyRatio', sortable: true },
        { key: 'nonPerformingLoanRatio', sortable: true },
        { key: 'liquidityRatio', sortable: true },
        { key: 'loanToDepositRatio', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      riskManagementFrameworkList: [],
      riskManagementFrameworkHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'riskManagementFrameworkUrl', sortable: true, label: 'Risk Management Framework' },
        { key: 'policiesUrl', sortable: true, label: 'Policies' },
        { key: 'limitsUrl', sortable: true, label: 'Limits' },
        { key: 'manualUrl', sortable: true, label: 'Manuals' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      loanPortfolioDetailsList: [],
      loanPortfolioDetailsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'creditPortfolioReport', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      aMLCFTComplianceList: [],
      aMLCFTComplianceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'antiMoneyLaundryUrl', sortable: true, label: 'Anti-Money Laundering' },
        { key: 'counterTerroristFinancingPoliciesUrl', sortable: true, label: 'Counter Terrorist Financing Policies' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      creditRiskManagementList: [],
      creditRiskManagementHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'creditRiskStrategyAndAnnualPortfolioPlan', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      regulatoryComplianceList: [],
      regulatoryComplianceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'regulatoryReturnsRenditionUrl', sortable: true, label: 'Regulatory Returns Rendition' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      regulatoryExaminationReportsList: [],
      regulatoryExaminationReportsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'cBNExaminationReportsUrl', sortable: true, label: 'CBN Examination Reports' },
        { key: 'nDICExaminationReportUrl', sortable: true, label: 'NDIC Examination Reports' },
        { key: 'managementResponseToExaminationReportUrl', sortable: true, label: 'Management Response To Examination Reports' },
        { key: 'managementResponseToNDICExaminationReportsUrl', sortable: true, label: 'Management Response To NDIC Examination Reports' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      riskAndAuditStructureList: [],
      riskAndAuditStructureHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'organogramUrl', sortable: true, label: 'Organogram' },
        { key: 'riskManagementUnitStaffDetails', sortable: true, label: 'Risk Management Unit Staff Details' },
        { key: 'internalAuditFunctionStaffDetails', sortable: true, label: 'Internal Audit Function Staff Details' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      ongoingRiskMonitoringList: [],
      ongoingRiskMonitoringHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'riskManagementReportsToBoardUrl', sortable: true, label: 'Risk Management Reports To Board' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      internalAuditFindingsList: [],
      internalAuditFindingsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'internalAuditsReportUrlLastYear', sortable: true, label: `Internal Audits Report ${lastYear}` },
        { key: 'internalAuditsReportUrlTwoYearsAgo', sortable: true, label: `Internal Audits Report ${lastTwoYears}` },
        { key: 'internalAuditsReportUrlThreeYearsAgo', sortable: true, label: `Internal Audits Report ${lastThreeYears}` },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      corporateGovernanceList: [],
      corporateGovernanceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'corporateGovernanceUrl', sortable: true, label: 'Corporate Governance Policies & Code of Conduct' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      collateralManagementList: [],
      collateralManagementHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'collateralValuationUrl', sortable: true, label: 'Collateral Valuation' },
        { key: 'nationalCollateralRegistryUrl', sortable: true, label: 'National Collateral Registry' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      creditBureauList: [],
      creditBureauHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'evidenceOfAgreement', sortable: true, label: 'Evidence of Agreement Credit Bureaus' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      customerComplaintHandlingList: [],
      customerComplaintHandlingHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'customerComplaintManagement', sortable: true, label: 'Customer Complaint Management System/Framework Bureaus' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      consumerCreditList: [],
      consumerCreditHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'copiesConsumerCredit', sortable: true, label: 'Copies of Consumer Credit Product Programs' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      consumerLoanPricingList: [],
      consumerLoanPricingHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'loanPricingStrategy', sortable: true, label: 'Loan Pricing Strategy for Consumer Credit Customers' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      externalBoardAssessmentList: [],
      externalBoardAssessmentHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'mostRecentExternalBoardEvaluation', sortable: true, label: 'Most recent External Board Evaluation/Assessment Report' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      itSystemAgreementsList: [],
      itSystemAgreementsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'signedAgreement', sortable: true, label: 'Signed Agreement with Banking Application and Other IT Vendor' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      taxDeductionList: [],
      taxDeductionHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'evidenceOfTaxRemittances', sortable: true },
        { key: 'evidenceOfPension', sortable: true },
        { key: 'evidenceOfOtherDeductions', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      ratingAgencyReportsList: [],
      ratingAgencyReportsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'ratingAgencyReport', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      internalRiskMonitoringList: [],
      internalRiskMonitoringHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'internalRiskMonitoring', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      environmentAndSocialRiskList: [],
      environmentAndSocialRiskHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'environmentalAndSocialRiskManagementPolicies', sortable: true },
        { key: 'relevantPoliciesFramework', sortable: true },
        { key: 'implementationEvidence', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      advancedRiskAnalysisList: [],
      advancedRiskAnalysisHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'stressTestReports', sortable: true },
        { key: 'marketSensitivityAnalysisReports', sortable: true },
        { key: 'liquidityContingencyFundingPlan', sortable: true },
        { key: 'businessContinuityAndDisasterRecoveryPlan', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      partnerCode: '',
      isCreditLimitBusy: false,
      creditLimitList: [],
      creditLimitHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'partnerCode', sortable: true, label: 'Partner Code' },
        { key: 'partnerName', sortable: true, label: 'Partner Name' },
        { key: 'approvedLimit', sortable: true, label: 'Approved Limit' },
        { key: 'status', sortable: true, label: 'Status' },
      ],
      creditLimitFormData: {
        creditLimit: null,
      },
      currentPartnerCode: '',
      creditLimitButton: 'Save',
      modalCreateLimitTitle: 'Set Credit Limit',
      modalMFATitle: 'Master Financing Agreement',
      mfaButton: 'Submit',
      masterFinancingAgreement: 'Master Financing Agreement',
      documentData: {
        title: 'Master Financing Agreement',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      },
      fileRules: 'required',
      mfaDocumentIdentifier: '',
      otherContactInfo: {
        institutionName: '',
        institutionEmail: '',
        institutionAddress: '',
        fullName: '',
        emailAddress: '',
        contactPhoneNumber: '',
        state: '',
        financialInstitutionType: '',
      },
      isPageShow: false,
      activeTab: 0,
      allValidationErrors: [],
    }
  },
  computed: {
    hasDocumentImage() {
      return Boolean(this.documentImageSrc)
    },
    hasKycDocumentImage() {
      return Boolean(this.kycDocumentImageSrc)
    },
    stateOptions() {
      const country = countries.find(item => item.name === 'Nigeria')
      return country.states.map(item => item.state).sort()
    },
    isImageFile() {
      const imageExtensions = ['png', 'jpeg', 'jpg', 'bmp', 'gif', 'svg', 'webp']
      return imageExtensions.includes(this.fileExtension)
    },
    isWordFile() {
      const wordExtensions = ['doc', 'docx', 'rtf']
      return wordExtensions.includes(this.fileExtension)
    },
    pdfViewerUrl() {
      return this.viewDocumentData
        ? `https://docs.google.com/viewer?url=${encodeURIComponent(this.viewDocumentData)}&embedded=true`
        : null
    },
    wordViewerUrl() {
      return this.viewDocumentData && this.isWordFile
        ? `https://docs.google.com/viewer?url=${encodeURIComponent(this.viewDocumentData)}&embedded=true`
        : null
    },
    dynamicMeanOfIDdatePickerConfig() {
      const baseConfig = {
        dateFormat: 'Y-m-d',
        ariaDateFormat: 'Y-m-d',
      }

      // If issue date is selected, use it as minDate
      // Otherwise, use today as fallback
      if (this.kycInformationFormData.meansOfIdentificationIssueDate) {
        baseConfig.minDate = this.kycInformationFormData.meansOfIdentificationIssueDate
      } else {
        baseConfig.minDate = 'today'
      }

      return baseConfig
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchPfiOnboarding()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchPfiOnboarding()
    },
    documentImage(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          if (typeof newValue === 'object') {
            base64Encode(newValue)
              .then(value => {
                this.documentImageSrc = value
              })
              .catch(() => {
                this.documentImageSrc = null
              })
          } else {
            this.documentImageSrc = newValue
          }
        } else {
          this.documentImageSrc = null
        }
      }
    },
    kycDocumentImage(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          if (typeof newValue === 'object') {
            base64Encode(newValue)
              .then(value => {
                this.kycDocumentImageSrc = value
              })
              .catch(() => {
                this.kycDocumentImageSrc = null
              })
          } else {
            this.kycDocumentImageSrc = newValue
          }
        } else {
          this.kycDocumentImageSrc = null
        }
      }
    },
    // Clear expiry date if it becomes invalid when issue date changes
    // eslint-disable-next-line func-names
    'kycInformationFormData.meansOfIdentificationIssueDate': function (newIssueDate) {
      const expiryDate = this.kycInformationFormData.meansOfIdentificationExpiryDate

      if (newIssueDate && expiryDate) {
        const issueDateTime = new Date(newIssueDate).getTime()
        const expiryDateTime = new Date(expiryDate).getTime()

        // If expiry date is before or equal to issue date, clear it
        if (expiryDateTime <= issueDateTime) {
          this.kycInformationFormData.meansOfIdentificationExpiryDate = null
        }
      }
    },
  },
  mounted() {
    this.fetchPfiOnboarding()
    this.mfaDocumentIdentifier = uuidv4()
  },
  methods: {
    ...mapActions({
      getPfiOnboarding: 'account/getPfiOnboardingList',
      downloadPfiOnboarding: 'account/downloadPfiOnboarding',
      uploadSupportingDocumentImages: 'account/uploadSupportingDocumentImages',
      getSupportingDocuments: 'account/getSupportingDocuments',
      uploadDocumentImages: 'account/uploadDocumentImagesAction',
      uploadDocumentFiles: 'account/uploadDocumentFilesAction',
      downloadKYCDocumentFiles: 'reports/downloadKYCDocumentFiles',
      deleteDocumentsUpload: 'account/deletePartnerDocumentAction',
      // deleteMFADocumentsUpload: 'account/deleteMFADocumentsUploadAction',
      getKYCInformation: 'account/getKYCInformation',
      createKYCDirector: 'account/createKYCDirectorAction',
      updateKYCDirector: 'account/updateKYCDirectorAction',
      createAllKYCInformation: 'account/createAllKYCInformation',
      deleteKYCDirectorAction: 'account/deleteKYCDirectorAction',
      validateBVN: 'eChannels/validateBVNAction',
      saveOrUpdateDueDiligence: 'llms/saveOrUpdateDueDiligenceAction',
      getDueDiligenceList: 'llms/getDueDiligenceList',
      submitDueDiligence: 'llms/submitDueDiligence',
      downloadOfferLetter: 'reports/downloadLoanApplicationLetter',
      getCreditLimits: 'account/getCreditLimits',
      createCreditLimit: 'account/createCreditLimit',
      uploadMFADocument: 'account/uploadMFADocument',
      getMasterFinancingAgreement: 'account/getMasterFinancingAgreement',
    }),
    resetValidationModal() {
      this.allValidationErrors = []
    },
    toggleBankNextTab() {
      this.activeTab += 1
    },
    toggleBankPreviousTab() {
      this.activeTab -= 1
    },
    async downloadMFA() {
      try {
        this.isBusy = true
        const response = await this.downloadOfferLetter({
          partnerCode: this.currentPartnerCode,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
          route: 'GetMasterFinancingAgreementTemplate',
        })
        if (response.status === 200) {
          const blob = new Blob(
            [response.data],
            { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
          )
          FileSaver.saveAs(blob, 'Master_Financing_Agreement.docx')
          this.alertSuccess({
            text: 'Your request to download Master Financing Agreement was successful',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async performMFA(item) {
      try {
        const binaryContent = new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2D])
        const fakeFile = new File(
          [binaryContent], 'Document.pdf',
          {
            type: 'application/pdf',
            lastModified: new Date().getTime(),
          },
        )
        this.$root.$emit('bv::show::modal', 'master-financing-agreement-modal')
        this.currentPartnerCode = item.partnerCode || item
        const response = await this.getMasterFinancingAgreement({
          partnerCode: item.partnerCode || item,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })
        const { status, data } = response
        if (status === 200 && data) {
          const [dataValue] = data
          if (dataValue.version === 'PFI') {
            this.documentData = {
              url: dataValue.url,
              fileExtension: dataValue.fileExtension,
              documentId: dataValue.documentId,
              file: dataValue.url ? fakeFile : null,
            }
          }
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    handleMFASubmit() {
      this.$refs.mfaRules.validate().then(success => {
        if (success) {
          this.confirm(this.performMFASubmit, { title: '', text: this.$t('Are you sure you want to Submit this Master Financing Agreement?') })
        } else {
          // Get all mfaRules Errors
          const errorMessages = []
          Object.keys(this.$refs.mfaRules.errors).forEach(key => {
            const value = this.$refs.mfaRules.errors[key]

            if (Array.isArray(value) && value.length > 0) {
              value.forEach(error => {
                errorMessages.push(`${error}`)
              })
            }
          })
          this.allValidationErrors = errorMessages
          this.$root.$emit('bv::show::modal', 'all-validated-modal')
        }
      })
    },
    async performMFASubmit() {
      try {
        this.showSpinner = true
        const documents = [{
          type: 'MasterFinancingAgreement',
          url: this.documentData.url,
          fileExtension: this.documentData.fileExtension,
          documentId: this.documentData.documentId,
        }]
        const formData = {
          documents,
          partnerCode: this.currentPartnerCode,
        }
        const response = await this.uploadMFADocument({ formData, permission: lmsPermissions.UPLOAD_MFA_DOCUMENT })
        const { status } = response
        if (status === 200) {
          this.alertSuccess({
            text: 'Your request to Save Master Financing Agreement was successful',
          })
          this.showSpinner = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to Save Master Financing Agreement failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.showSpinner = false
      }
    },
    resetMFAFormModal() {
      this.documentData = { ...null }
    },
    async handleFileUpload(event) {
      const { documentData } = this
      const { files } = event.target

      documentData.validationError = ''

      if (!files || !files[0]) {
        documentData.validationError = 'Please select a file.'
        return
      }

      const file = files[0]

      const maxSizeMB = 5
      if (file.size > maxSizeMB * 1024 * 1024) {
        documentData.validationError = `File size exceeds ${maxSizeMB}MB limit.`
        return
      }

      try {
        documentData.loading = true

        const formData = new FormData()
        formData.append('attachedFile', file)
        formData.append('type', 'Others')
        formData.append('referenceNumber', this.mfaDocumentIdentifier)

        const response = await this.uploadDocumentImages({
          formData,
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
        })

        const { status, data } = response
        if (status === 200 && data) {
          let extensionType
          if (data.fileExtension === 'vnd.openxmlformats-officedocument.wordprocessingml.document') {
            extensionType = 'docx'
          } else if (data.fileExtension === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            extensionType = 'xlsx'
          } else {
            extensionType = null
          }

          documentData.url = data.url
          documentData.documentId = data.id
          documentData.fileExtension = extensionType !== null ? extensionType : data.fileExtension

          this.toastAlert({
            title: this.$t('Document uploaded successfully'),
          })
        }
      } catch (error) {
        error.response.data.errors.forEach(err => {
          this.alertError({
            text: `Upload failed: ${err.errorMessage}`,
          })
        })
      } finally {
        documentData.loading = false
      }
    },
    async downloadKYCDocumentTemplate(route) {
      try {
        this.isBusy = true
        const routeMap = {
          GetNDA: 'Non-Disclosure Agreement',
          GetDirectDebitMandate: 'Direct Debit Mandate',
          GetWolfsbergQuestionnaire: 'Anti-Money Laundering Questionnaire',
          GetDeclarationOfUBO: 'Declaration of Ultimate Beneficial Ownership',
          GetSignatureMandate: 'Signature Mandate',
        }
        const currentRoute = routeMap[route] || 'Board Resolution'

        const fileName = currentRoute.replace(/\s+/g, '_')
        const response = await this.downloadKYCDocumentFiles({
          partnerCode: this.partnerCode,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
          route,
        })
        if (response.status === 200) {
          const blob = new Blob(
            [response.data],
            { type: 'application/msword' },
          )
          FileSaver.saveAs(blob, `${fileName}.docx`)
          this.alertSuccess({
            text: `Your request to download ${currentRoute} was successful`,
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async handleKYCFileUpload(event, fileObjectPath, imageType) {
      const documentFilePath = this[fileObjectPath]

      const { files } = event.target

      documentFilePath.validationError = ''
      this.kycDocumentIdentifier = uuidv4()

      if (!files || !files[0]) {
        documentFilePath.validationError = 'Please select a file.'
        return
      }

      const file = files[0]
      const maxSizeMB = 5
      if (file.size > maxSizeMB * 1024 * 1024) {
        documentFilePath.validationError = `File size exceeds ${maxSizeMB}MB limit.`
        return
      }

      try {
        documentFilePath.loading = true

        const formData = new FormData()
        formData.append('attachedFile', file)
        formData.append('type', 'Others')
        formData.append('referenceNumber', this.kycDocumentIdentifier)

        const response = await this.uploadDocumentImages({
          formData,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })

        const { status, data } = response
        if (status === 200 && data) {
          let extensionType
          if (data.fileExtension === 'vnd.openxmlformats-officedocument.wordprocessingml.document') {
            extensionType = 'docx'
          } else if (data.fileExtension === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            extensionType = 'xlsx'
          } else {
            extensionType = null
          }

          documentFilePath.url = data.url
          documentFilePath.documentId = data.id
          documentFilePath.fileExtension = extensionType !== null ? extensionType : data.fileExtension
          documentFilePath.type = imageType

          documentFilePath.loading = false

          this.toastAlert({
            title: this.$t('Document uploaded successfully'),
          })
        }
      } catch (error) {
        error.response.data.errors.forEach(err => {
          this.alertError({
            text: `Upload failed: ${err.errorMessage}`,
          })
        })
      } finally {
        documentFilePath.loading = false
      }
    },
    async removeDocument(id) {
      try {
        this.documentData.loading = true
        this.documentLoadSpinner = true
        const formData = {
          documentId: id,
        }
        const response = await this.deleteDocumentsUpload({
          formData,
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
        })
        const { status } = response
        if (status === 200) {
          this.alertSuccess({ text: 'Document Removed' })
          this.documentData.url = ''
          this.documentData.loading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `Delete failed, Reason: ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.documentData.loading = false
      }
    },
    async removeKycDocument(id, category) {
      try {
        if (category === 'SignatureMandate') {
          this.signatureMandateFile.loading = true
        } else if (category === 'DueDiligenceMandate') {
          this.directDebitMandateFile.loading = true
        } else if (category === 'WolfsbergQuestionnaire') {
          this.antiMoneyLaunderingQuestionnaireFile.loading = true
        } else if (category === 'DeclarationOfUBO') {
          this.declarationOfUltimateBeneficialOwnershipFile.loading = true
        } else if (category === 'NonDisclosureAgreement') {
          this.nonDisclosureAgreementFile.loading = true
        } else if (category === 'BoardResolutionTemplate') {
          this.boardResolutionFile.loading = true
        } else if (category === 'DirectorValidID') {
          this.kycDirectorFile.loading = true
        } else {
          this.boardResolutionFile.loading = true
        }
        const formData = {
          documentId: id,
        }
        const response = await this.deleteDocumentsUpload({
          formData,
          permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
        })
        const { status } = response
        if (status === 200) {
          this.alertSuccess({ text: 'Document Removed' })
          this.fetchKYCInformation(this.dataItems)
          if (category === 'SignatureMandate') {
            this.signatureMandateFile.loading = false
            this.signatureMandateFile.url = ''
          } else if (category === 'DueDiligenceMandate') {
            this.directDebitMandateFile.loading = false
            this.directDebitMandateFile.url = ''
          } else if (category === 'WolfsbergQuestionnaire') {
            this.antiMoneyLaunderingQuestionnaireFile.loading = false
            this.antiMoneyLaunderingQuestionnaireFile.url = ''
          } else if (category === 'DeclarationOfUBO') {
            this.declarationOfUltimateBeneficialOwnershipFile.loading = false
            this.declarationOfUltimateBeneficialOwnershipFile.url = ''
          } else if (category === 'NonDisclosureAgreement') {
            this.nonDisclosureAgreementFile.loading = false
            this.nonDisclosureAgreementFile.url = ''
          } else if (category === 'BoardResolutionTemplate') {
            this.boardResolutionFile.loading = false
            this.boardResolutionFile.url = ''
          } else if (category === 'DirectorValidID') {
            this.kycDirectorFile.loading = false
            this.kycDirectorFile.url = ''
          } else {
            this.boardResolutionFile.loading = false
            this.boardResolutionFile.url = ''
          }
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `Delete failed, Reason: ${responseError.errorMessage}`,
          })
        })
      } finally {
        if (category === 'SignatureMandate') {
          this.signatureMandateFile.loading = false
          this.signatureMandateFile.url = ''
        } else if (category === 'DueDiligenceMandate') {
          this.directDebitMandateFile.loading = false
          this.directDebitMandateFile.url = ''
        } else if (category === 'WolfsbergQuestionnaire') {
          this.antiMoneyLaunderingQuestionnaireFile.loading = false
          this.antiMoneyLaunderingQuestionnaireFile.url = ''
        } else if (category === 'DeclarationOfUBO') {
          this.declarationOfUltimateBeneficialOwnershipFile.loading = false
          this.declarationOfUltimateBeneficialOwnershipFile.url = ''
        } else if (category === 'NonDisclosureAgreement') {
          this.nonDisclosureAgreementFile.loading = false
          this.nonDisclosureAgreementFile.url = ''
        } else if (category === 'BoardResolutionTemplate') {
          this.boardResolutionFile.loading = false
          this.boardResolutionFile.url = ''
        } else if (category === 'DirectorValidID') {
          this.kycDirectorFile.loading = false
          this.kycDirectorFile.url = ''
        } else {
          this.boardResolutionFile.loading = false
          this.boardResolutionFile.url = ''
        }
      }
    },
    async manageCreditLimit(item) {
      try {
        this.otherContactInfo = {
          institutionName: item.institutionName,
          institutionEmail: item.institutionEmail,
          institutionAddress: item.institutionAddress,
          fullName: item.fullName,
          emailAddress: item.emailAddress,
          contactPhoneNumber: item.contactPhoneNumber,
          state: item.state,
          financialInstitutionType: item.financialInstitutionType,
        }
        this.$root.$emit('bv::show::modal', 'credit-limit-modal')
        this.currentPartnerCode = item.partnerCode || item
        this.isCreditLimitBusy = true
        const response = await this.getCreditLimits({
          partnerCode: item.partnerCode || item,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })
        const { status, data } = response
        if (status === 200 && data) {
          this.creditLimitFormData = {
            creditLimit: data.approvedLimit ? toBalance(data.approvedLimit) : '',
          }
          // Convert Objects to array of object
          const result = {
            data: [data],
          }
          this.creditLimitList = result.data[0].approvedLimit > 0 ? result.data : []
          if (this.creditLimitList.length > 0) {
            this.creditLimitButton = 'Update'
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isCreditLimitBusy = false
      }
    },
    async handleCreditLimit(formData) {
      this.confirm(() => this.createLimitAction(formData), { text: `Are you sure you want to ${this.creditLimitButton} this Credit Limit?` })
    },
    async createLimitAction(val) {
      try {
        this.isCreditLimitBusy = true

        const formData = {
          ...this.otherContactInfo,
          creditLimit: val.creditLimit ? toKobo(val.creditLimit) : '',
          partnerCode: this.currentPartnerCode,
        }
        const response = await this.createCreditLimit({ formData, permission: lmsPermissions.SETUP_CREDIT_LIMIT })
        const { status } = await response
        if (status === 200) {
          this.manageCreditLimit(this.currentPartnerCode)
          this.alertSuccess({ text: `Your request to ${this.creditLimitButton} Credit Limit was successful` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to ${this.creditLimitButton} Credit Limit failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.isCreditLimitBusy = false
      }
    },
    async downloadOfferLetters(item) {
      try {
        this.isBusy = true
        const response = await this.downloadOfferLetter({
          partnerCode: item.partnerCode,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
          route: 'GetOfferLetter',
        })
        if (response.status === 200) {
          const blob = new Blob(
            [response.data],
            { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
          )
          FileSaver.saveAs(blob, 'Offer_Letter.docx')
          this.alertSuccess({
            text: 'Your request to download the Offer Letter was successful. Please proceed to the KYC stage to continue your application.',
          })
          this.fetchPfiOnboarding()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    validateKycForm() {
      return this.$refs.kycInformation.validate()
    },

    updateKycData(newData) {
      this.$refs.kycInformation.updateData(newData)
    },

    handleFormUpdate(formData) {
      this.kycInformationFormData = { ...formData }
    },
    addNewRiskManagementUnitStaffDetails() {
      this.dueDiligence.compulsoryDocument.riskAndAuditStructure.riskManagementUnitStaffDetails.staffDetails.push({
        fullName: '',
        designation: '',
        qualifications: [],
        relevantWorkingExperience: '',
      })
    },
    removeRiskManagementUnitStaffDetails(index) {
      this.dueDiligence.compulsoryDocument.riskAndAuditStructure.riskManagementUnitStaffDetails.staffDetails.splice(index, 1)
    },
    addNewInternalAuditFunctionStaffDetails() {
      this.dueDiligence.compulsoryDocument.riskAndAuditStructure.internalAuditFunctionStaffDetails.staffDetails.push({
        fullName: '',
        designation: '',
        qualifications: [],
        relevantWorkingExperience: '',
      })
    },
    removeInternalAuditFunctionStaffDetails(index) {
      this.dueDiligence.compulsoryDocument.riskAndAuditStructure.internalAuditFunctionStaffDetails.staffDetails.splice(index, 1)
    },
    onRiskManagementUnitStaffDetails(items) {
      this.riskManagementStaffInformationList = items.staffDetails.map(item => ({
        ...item,
        qualifications: this.qualificationsOptions
          .filter(opt => item.qualifications.includes(Number(opt.value)))
          .map(opt => opt.label).join(', '),
        documentUrl: items.documentUrl,
      }))
      this.$root.$emit('bv::show::modal', 'risk-management-unit-staff-details-modal')
    },
    onInternalAuditFunctionStaffDetails(items) {
      this.internalAuditFunctionStaffInformationList = items.staffDetails.map(item => ({
        ...item,
        qualifications: this.qualificationsOptions
          .filter(opt => item.qualifications.includes(Number(opt.value)))
          .map(opt => opt.label).join(', '),
        documentUrl: items.documentUrl,
      }))
      this.$root.$emit('bv::show::modal', 'internal-audit-unit-staff-details-modal')
    },

    extractFileFromEvent(event) {
      if (event.target && event.target.files && event.target.files.length > 0) {
        return event.target.files[0]
      } if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        return event.dataTransfer.files[0] // For drag and drop
      } if (event instanceof File) {
        return event // In case the file object is passed directly
      } if (event instanceof FileList && event.length > 0) {
        return event[0] // In case a FileList is passed
      } if (Array.isArray(event) && event.length > 0 && event[0] instanceof File) {
        return event[0] // In case an array of Files is passed
      }
      return null
    },

    async onSelectFile(event, type) {
      try {
        // 1. Extract the file using a helper method
        const file = this.extractFileFromEvent(event)

        if (!file) {
          console.error('No file selected')
          return
        }

        // Get the target path from the map
        const targetPath = documentPathMap[type]
        if (!targetPath) {
          this.alertError({ text: this.$t('An error occurred (unknown upload type).') })
          return
        }

        // 2. Prepare and send the upload request
        const formData = new FormData()
        formData.append('attachedFile', file)
        this.documentIdentifier = uuidv4()
        formData.append('type', 'Others')
        formData.append('referenceNumber', this.documentIdentifier)

        const response = await this.uploadDocumentImages({
          formData,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })

        // 3. Handle the response
        const { status, data } = response
        if (status === 200 && data.url) {
          // Update the appropriate field using a mapping object instead of a switch
          this.setNestedProperty(this.dueDiligence, targetPath, data.url)
          this.toastAlert({
            title: this.$t('Document uploaded successfully'),
          })
        } else {
          // Handle non-200 status or missing URL in response
          const errorMessage = data?.message || this.$t('Failed to upload document. Please try again.')
          this.alertError({ text: errorMessage })
        }
      } catch (error) {
        this.handleUploadError(error)
      }
    },

    handleUploadError(error) {
      if (error.response && error.response.data && error.response.data.errors) {
        const text = this.$t('Your request to upload image failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${text} ${responseError.errorMessage}` })
        })
      } else {
        this.alertError({ text: this.$t('Failed to upload document. Please try again.') })
      }
    },
    async bvnValidateAction(item) {
      try {
        this.isKycLoading = true
        this.showSpinner = true
        const formData = {
          bvnNumber: item.bvn,
        }
        const response = await this.validateBVN({ formData, permission: accountPermissions.VALIDATE_BVN })
        const { data, status } = response
        if (status === 200 && data.result) {
          this.alertSuccess({ text: 'Your request to validate BVN was successful?' })
          this.showSpinner = false
          this.isKycLoading = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
        this.isLoading = false
      }
    },
    getLGAByState(selectedValue) {
      this.kycInformationFormData.localGovtCode = ''
      const country = countries.find(item => item.name === 'Nigeria')
      const selectedState = country.states.find(item => item.state === selectedValue)
      this.lgaOptions = selectedState.lgas.sort()
    },
    selectPFIOnboardingItems(item, activity, val) {
      this.$router.push({
        name: 'pfi-onboarding',
        params: {
          type: val,
          id: item.id,
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    getFileExtension(url) {
      try {
        const urlObj = new URL(url)
        const params = new URLSearchParams(urlObj.search)

        const documentId = params.get('DocumentId')
        if (!documentId) return null

        const parts = documentId.split('.')
        return parts.length > 1 ? parts.pop() : null
      } catch (err) {
        console.error('Invalid URL:', err)
        return null
      }
    },

    async handleDocumentDownload(url) {
      try {
        this.isBusy = true

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: '*/*',
          },
        })

        if (response.ok) {
          const blob = await response.blob()
          // Extract filename from URL or use title
          const filename = new URL(url).pathname.split('/').pop()
          const extension = this.getFileExtension(url)
          if (extension === 'document') {
            FileSaver.saveAs(blob, `${filename}.docx`)
          } else if (extension === 'sheet') {
            FileSaver.saveAs(blob, `${filename}.xlsx`)
          } else {
            FileSaver.saveAs(blob, `${filename}.csv`)
          }
          this.alertSuccess({
            text: 'Your request to download file was successful',
          })
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    handleDownload(url) {
      try {
        const extension = this.getFileExtension(url)
        if (extension === 'document' || extension === 'sheet' || extension === 'csv') {
          this.handleDocumentDownload(url)
        } else {
          const link = document.createElement('a')
          link.href = url

          // Extract filename from URL or use title
          const filename = new URL(url).pathname.split('/').pop()
      || this.viewDocumentModalTitle.replace(/\s+/g, '_')

          link.setAttribute('download', filename)
          link.style.display = 'none'
          document.body.appendChild(link)

          // Trigger download
          link.click()
          this.alertSuccess({ text: 'Your request to download file was successful' })

          // Clean up
          document.body.removeChild(link)
        }
      } catch (error) {
        this.alertError({ text: 'Download may have been blocked. Please check your browser.' })
      }
    },
    viewSupportingDocumentFile(image, title, fileExtension) {
      this.viewDocumentData = image
      this.fileExtension = fileExtension
      this.viewDocumentModalTitle = title ? title.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ') : ''
      this.$root.$emit('bv::show::modal', 'view-avatar-modal')
    },
    viewDocumentFile(image, title) {
      this.viewDocumentData = image
      this.viewDocumentModalTitle = title ? title.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ') : ''
      this.$root.$emit('bv::show::modal', 'view-avatar-modal')
    },
    onViewDocumentFile(url, title) {
      this.showSpinnerDoc = true
      this.fileExtension = null
      this.viewDocumentData = null
      const params = new URL(url).searchParams
      const fileName = params.get('DocumentId')
      const ext = fileName.split('.').pop()
      this.fileExtension = ext
      // this.viewDocumentData = url
      this.viewDocumentModalTitle = title

      this.loadDocument(url)
      // this.$root.$emit('bv::show::modal', 'view-document-modal')
    },
    onDocumentLoaded() {
      this.showSpinnerDoc = false
    },
    onDocumentError() {
      this.showSpinnerDoc = false
      // this.alertError({ text: 'Failed to load document.' })
    },
    loadDocument(url) {
      // For images, preload to handle loading state
      if (['png', 'jpeg', 'jpg', 'bmp'].includes(this.fileExtension)) {
        const img = new Image()
        img.onload = () => {
          this.viewDocumentData = url
          this.showSpinnerDoc = false
          this.$root.$emit('bv::show::modal', 'view-document-modal')
        }
        img.onerror = () => {
          this.showSpinnerDoc = false
          this.alertError({ text: 'Failed to load image' })
        }
        img.src = url
      } else { // For PDFs and other files
        this.viewDocumentData = url
        this.showSpinnerDoc = false
        this.$root.$emit('bv::show::modal', 'view-document-modal')
      }
    },
    async uploadSupportingDocuments(item) {
      this.documentIdentifier = item.partnerCode
      try {
        this.showSpinnerDoc = true
        this.isKycBusy = true
        this.$root.$emit('bv::show::modal', 'upload-supporting-document-modal')
        const response = await this.getSupportingDocuments({
          partnerCode: item.partnerCode,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.uploadSupportingDocList = data.data
          this.totalCount = data.recordCount
          this.showSpinnerDoc = false
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isKycBusy = false
        this.showSpinnerDoc = false
      }
    },
    uploadDocumentFile(item) {
      this.supportingDocumentType = item.name
      this.documentImageSrc = item.url
      this.fileExtension = item.documentExtension
      this.$root.$emit('bv::show::modal', 'upload-document-file-modal')
    },
    selectKYCInformationItems(item, _, index, val) {
      this.isReadonly = val !== 'edit'
      this.isPageShow = val === 'edit' || val === 'view'
      this.currentIndex = index
      this.currentValue = val
      this.actionType = val
      this.directorButtonLabel = val === 'edit' ? 'Update Director' : 'Add Director'
      this.isKycReadonly = val === 'edit' || val === 'view'
      this.kycInformationFormData = {
        lastName: item.lastName,
        firstName: item.firstName,
        middleName: item.middleName,
        title: item.title,
        phoneNumber: item.phoneNumber,
        emailAddress: item.emailAddress,
        bvn: item.bvn,
        pep: item.pep,
        meansOfIdentification: item.meansOfIdentification,
        idNumber: item.idNumber,
        meansOfIdentificationIssueDate: item.meansOfIdentificationIssueDate,
        meansOfIdentificationExpiryDate: item.meansOfIdentificationExpiryDate,
        meansOfIdentificationPlaceOfIssue: item.meansOfIdentificationPlaceOfIssue,
        documentIdentifier: item.documentIdentifier,
        status: item.status,
        id: item.id,
      }
      const binaryContent = new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2D])
      const fakeFile = new File(
        [binaryContent], 'Document.pdf',
        {
          type: 'application/pdf',
          lastModified: new Date().getTime(),
        },
      )
      this.kycDirectorFile = {
        ...this.kycDirectorFile,
        url: item.validID.documentURL,
        fileExtension: item.validID.fileExtension,
        documentId: item.validID.documentId,
        type: item.validID.documentType,
        validIDStatus: item.validID.validIDStatus,
        file: fakeFile,
      }
      this.kycDocumentImageSrc = item.documents[0].url
    },

    async deleteKYCDirector(item) {
      try {
        const deleteDirector = async () => {
          this.isKycBusy = true
          const formData = {
            directorId: item.id,
          }
          const response = await this.deleteKYCDirectorAction({
            formData,
            permission: lmsPermissions.VIEW_EXTERNAL_LOAN_ACCOUNT,
          })
          const { status } = response
          if (status === 200) {
            this.alertSuccess({ text: 'Director Removed' })
            this.fetchKYCInformation(item)
            this.isKycBusy = false
          }
        }
        this.confirm(deleteDirector, { title: '', text: 'Are you sure you want to delete this Director?' })
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `Delete failed, Reason: ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.isKycBusy = false
      }
    },
    resetKYCInformationForm() {
      this.kycInformationFormData = { ...null }
      this.kycInformationList = []
      this.nonDisclosureAgreementFile = {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      }
      this.directDebitMandateFile = {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      }
      this.antiMoneyLaunderingQuestionnaireFile = {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      }
      this.declarationOfUltimateBeneficialOwnershipFile = {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      }
      this.signatureMandateFile = {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      }
      this.boardResolutionFile = {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      }
      this.isPageShow = false
    },
    deleteDocumentImage() {
      this.documentImage = null
      this.documentImageSrc = ''
    },
    clearKycDocumentImage() {
      this.kycDocumentImage = null
      this.kycDocumentImageSrc = ''
    },
    async onDocumentFileUpload(event) {
      try {
        this.hideModal('upload-document-file-modal')
        this.uploadLoadSpinner = true
        const { target } = event
        const formData = new FormData()
        if (typeof target.files[0] !== 'string') {
          formData.append('attachedFile', target.files[0])
          formData.append('type', this.supportingDocumentType)
          formData.append('referenceNumber', this.documentIdentifier)
        }
        const response = await this.uploadSupportingDocumentImages({ formData, permission: lmsPermissions.VIEW_PFI_ONBOARDING })
        const { status, data } = await response
        if (status === 200 && data) {
          this.fileExtension = data.fileExtension
          this.documentImageSrc = data.url
          this.uploadLoadSpinner = false
          const newItem = {
            // Passing saved partnerCode to method
            partnerCode: this.documentIdentifier,
          }
          this.uploadSupportingDocuments(newItem)
          this.$root.$emit('bv::show::modal', 'upload-supporting-document-modal')
          this.alertSuccess({ text: 'Your request to upload a file document was successful' })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Image upload failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.uploadLoadSpinner = false
      }
    },
    async handleDocumentUploaded({ formData, fieldType, documentIdentifier }) {
      console.log({ fieldType, documentIdentifier })
      try {
        const response = await this.uploadDocumentImages({
          formData,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })
        const { status, data } = await response
        if (status === 200 && data) {
          // Update the document URL in the child component
          this.$refs.kycInformationComponent.updateDocumentUrl(data.url)
        }
      } catch (error) {
        if (error.response?.data?.errors) {
          error.response.data.errors.forEach(responseError => {
            this.alertError({ text: `Image upload failed. Reason: ${responseError.errorMessage}` })
          })
        } else {
          this.alertError({ text: 'Image upload failed' })
        }
      }
    },
    async onKycDocumentFileUpload(event) {
      try {
        if (this.kycInformationFormData.meansOfIdentification) {
          this.kycDocumentIdentifier = uuidv4()
          this.kycDirectorFile.loading = true
          const { target } = event
          const formData = new FormData()
          if (typeof target.files[0] !== 'string') {
            formData.append('attachedFile', target.files[0])
            formData.append('type', this.kycInformationFormData.meansOfIdentification)
            formData.append('referenceNumber', this.kycDocumentIdentifier)
          }
          const response = await this.uploadDocumentImages({ formData, permission: lmsPermissions.VIEW_PFI_ONBOARDING })

          const { status, data } = await response
          if (status === 200 && data) {
            let extensionType
            if (data.fileExtension === 'vnd.openxmlformats-officedocument.wordprocessingml.document') {
              extensionType = 'docx'
            } else if (data.fileExtension === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
              extensionType = 'xlsx'
            } else {
              extensionType = null
            }
            this.kycDirectorFile.url = data.url
            this.kycDirectorFile.documentId = data.id
            this.kycDirectorFile.fileExtension = extensionType !== null ? extensionType : data.fileExtension
            this.kycDirectorFile.loading = false
          }
        } else {
          this.alertError({ text: 'Please select a Means of Identification first' })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Image upload failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.kycDirectorFile.loading = false
      }
    },
    async fetchPfiOnboarding() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getPfiOnboarding({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.pfiOnboardingList = data.data.map(item => ({
            ...item,
            institutionName: item.backgroundInformation.institutionName,
            institutionEmail: item.backgroundInformation.institutionEmail,
            institutionAddress: item.backgroundInformation.institutionAddress,
            contactPhoneNumber: item.contactInfo[0].contactPhoneNumber,
            fullName: item.contactInfo[0].fullName,
            roleWithinTheOrganization: item.contactInfo[0].roleWithinTheOrganization,
            city: item.backgroundInformation.city,
            state: item.backgroundInformation.state,
            localGovernmentArea: item.backgroundInformation.localGovernmentArea,
            shareholdersFund: item.backgroundInformation.shareholdersFund,
            financialInstitutionType: item.backgroundInformation.financialInstitutionType,
            operatingLicenseIssueDate: item.backgroundInformation.operatingLicenseIssueDate,
            operationCommencementDate: item.backgroundInformation.operationCommencementDate,
          }))
          this.totalCount = data.recordCount
        } else {
          this.pfiOnboardingList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchKYCInformation(item) {
      try {
        this.dataItems = item
        this.kycBankFormData = {
          bankName: item?.institutionName,
          institutionEmail: item?.institutionEmail,
          institutionAddress: item?.institutionAddress,
        }

        this.partnerCode = item?.partnerCode
        this.isKycBusy = true
        this.$root.$emit('bv::show::modal', 'kyc-information-modal')
        const filter = {
          ...this.filter,
          partnerCode: item.partnerCode,
        }
        const response = await this.getKYCInformation({
          search: filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })
        const { status, data } = response
        if (status === 200) {
          if (data.data.length !== 0) {
            this.kycInformationList = data.data.map(items => ({
              ...items,
              lastName: items.surName,
            }))

            this.kycBankFormData = {
              ...this.kycBankFormData,
              tin: data.tin,
            }
            this.totalCount = data.recordCount
          }

          const documentMapping = {
            NonDisclosureAgreement: 'nonDisclosureAgreementFile',
            DueDiligenceMandate: 'directDebitMandateFile',
            WolfsbergQuestionnaire: 'antiMoneyLaunderingQuestionnaireFile',
            DeclarationOfUBO: 'declarationOfUltimateBeneficialOwnershipFile',
            SignatureMandate: 'signatureMandateFile',
            BoardResolutionTemplate: 'boardResolutionFile',
          }

          const createEmptyDocument = () => ({
            type: '',
            file: null,
            url: '',
            fileExtension: '',
            loading: false,
            validationError: '',
            documentId: '',
          })

          const resetAllDocuments = () => {
            Object.values(documentMapping).forEach(propName => {
              this[propName] = createEmptyDocument()
            })
          }

          const binaryContent = new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2D])
          const fakeFile = new File(
            [binaryContent], 'Document.pdf',
            {
              type: 'application/pdf',
              lastModified: new Date().getTime(),
            },
          )

          data.documents.forEach(doc => {
            const propertyName = documentMapping[doc.name]

            if (propertyName) {
              this[propertyName] = { ...doc, type: doc.name, file: doc.url ? fakeFile : null }
            } else {
              resetAllDocuments()
            }
          })
          this.isKycBusy = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isKycBusy = false
      }
    },
    async addNewKYCDirectors() {
      this.$refs.kycInformationRules.validate().then(async success => {
        if (success) {
          this.showSpinner = true
          try {
            const directors = [{
              ...this.kycInformationFormData,
              bankName: this.kycBankFormData.bankName,
              institutionEmail: this.kycBankFormData.institutionEmail,
              institutionAddress: this.kycBankFormData.institutionAddress,
              directorID: {
                url: this.kycDirectorFile.url,
                documentId: this.kycDirectorFile.documentId,
                type: this.kycInformationFormData.meansOfIdentification,
                fileExtension: this.kycDirectorFile.fileExtension,
                expiryDate: this.kycInformationFormData.meansOfIdentificationExpiryDate,
                documentName: this.kycInformationFormData.meansOfIdentification,
              },
            }]

            const formData = {
              directors,
              partnerCode: this.partnerCode,
            }
            const response = this.actionType === 'add'
              ? await this.createKYCDirector({ formData, permission: lmsPermissions.VIEW_PFI_ONBOARDING })
              : await this.updateKYCDirector({ formData, permission: lmsPermissions.VIEW_PFI_ONBOARDING })
            const { status } = response
            if (status === 200) {
              this.alertSuccess({
                text: `Your request to ${this.directorButtonLabel} KYC Information was successful`,
              })
              this.fetchKYCInformation(this.dataItems)
              // this.actionType = this.actionType === 'add' ? 'add' : 'edit'
              if (this.actionType === 'edit') {
                this.actionType = 'add'
                this.directorButtonLabel = 'Add Director'
              }
              this.resetFormData()
              this.showSpinner = false
            }
            this.isKycReadonly = false
          } catch (error) {
            error.response.data.errors.forEach(responseError => {
              this.alertError({ text: `Your request to ${this.directorButtonLabel} KYC Information failed. Reason: ${responseError.errorMessage}` })
            })
          } finally {
            this.showSpinner = false
          }
        } else {
          // Get all kycInformationRules Errors
          const errorMessages = []
          Object.keys(this.$refs.kycInformationRules.errors).forEach(key => {
            const value = this.$refs.kycInformationRules.errors[key]

            if (Array.isArray(value) && value.length > 0) {
              value.forEach(error => {
                errorMessages.push(`${error}`)
              })
            }
          })
          this.allValidationErrors = errorMessages
          this.$root.$emit('bv::show::modal', 'all-validated-modal')
        }
      })
    },
    resetFormData() {
      this.$refs.kycInformationRules.reset()
      // Reset your reactive data
      this.kycInformationFormData = {
        ...null,
      }
      this.kycDirectorFile = {
        type: '',
        file: null,
        url: '',
        fileExtension: '',
        loading: false,
        validationError: '',
        documentId: '',
      }
    },
    handleAllKYCInformation() {
      this.$refs.institutionDocumentRules.validate().then(async success => {
        if (success) {
          this.confirm(this.performAllKYCInformation, { title: '', text: this.$t('Are you sure you want to Submit this KYC Information?') })
        } else {
          // Get all institutionDocumentRules Errors
          const errorMessages = []
          Object.keys(this.$refs.institutionDocumentRules.errors).forEach(key => {
            const value = this.$refs.institutionDocumentRules.errors[key]

            if (Array.isArray(value) && value.length > 0) {
              value.forEach(error => {
                errorMessages.push(`${error}`)
              })
            }
          })
          this.allValidationErrors = errorMessages
          this.$root.$emit('bv::show::modal', 'all-validated-modal')
        }
      })
    },
    async performAllKYCInformation() {
      this.showSpinner = true
      try {
        const directors = this.kycInformationList.map(item => ({
          ...item,
          bankName: this.kycBankFormData.bankName,
          institutionEmail: this.kycBankFormData.institutionEmail,
          institutionAddress: this.kycBankFormData.institutionAddress,
          directorID: item.validID ? {
            url: item.validID.documentURL,
            documentId: item.validID.documentId,
            type: item.validID.documentType,
            fileExtension: item.validID.fileExtension,
            expiryDate: item.validID.expiryDate,
            documentName: item.validID.documentName,
          } : null,
          validID: undefined, // This will remove the validID property
        }))

        const documents = [
          {
            type: this.nonDisclosureAgreementFile.type,
            url: this.nonDisclosureAgreementFile.url,
            documentId: this.nonDisclosureAgreementFile.documentId,
            fileExtension: this.nonDisclosureAgreementFile.fileExtension,
            tin: this.kycBankFormData.tin,
            partnerCode: this.partnerCode,
          },
          {
            url: this.directDebitMandateFile.url,
            type: this.directDebitMandateFile.type,
            fileExtension: this.directDebitMandateFile.fileExtension,
            documentId: this.directDebitMandateFile.documentId,
            tin: this.kycBankFormData.tin,
            partnerCode: this.partnerCode,
          },
          {
            url: this.antiMoneyLaunderingQuestionnaireFile.url,
            type: this.antiMoneyLaunderingQuestionnaireFile.type,
            fileExtension: this.antiMoneyLaunderingQuestionnaireFile.fileExtension,
            documentId: this.antiMoneyLaunderingQuestionnaireFile.documentId,
            tin: this.kycBankFormData.tin,
            partnerCode: this.partnerCode,
          },
          {
            url: this.declarationOfUltimateBeneficialOwnershipFile.url,
            type: this.declarationOfUltimateBeneficialOwnershipFile.type,
            fileExtension: this.declarationOfUltimateBeneficialOwnershipFile.fileExtension,
            documentId: this.declarationOfUltimateBeneficialOwnershipFile.documentId,
            tin: this.kycBankFormData.tin,
            partnerCode: this.partnerCode,
          },
          {
            url: this.signatureMandateFile.url,
            type: this.signatureMandateFile.type,
            fileExtension: this.signatureMandateFile.fileExtension,
            documentId: this.signatureMandateFile.documentId,
            tin: this.kycBankFormData.tin,
            partnerCode: this.partnerCode,
          },
          {
            url: this.boardResolutionFile.url,
            type: this.boardResolutionFile.type,
            fileExtension: this.boardResolutionFile.fileExtension,
            documentId: this.boardResolutionFile.documentId,
            tin: this.kycBankFormData.tin,
            partnerCode: this.partnerCode,
          },
        ]
        const formData = {
          directorsRequest: {
            directors,
            partnerCode: this.partnerCode,
            tin: this.kycBankFormData.tin,
          },
          documents,
        }
        const response = await this.createAllKYCInformation({ formData, permission: lmsPermissions.SUBMIT_DIRECTORS_KYC })
        const { status } = response
        if (status === 200) {
          this.alertSuccess({
            text: 'Your request to Save KYC Information was successful',
          })
          this.showSpinner = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to Save KYC Information failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.showSpinner = false
      }
    },
    async onViewDueDiligence(item) {
      try {
        this.showSpinner = true
        this.partnerCode = item?.partnerCode

        const response = await this.getDueDiligenceList({
          search: { partnerCode: item.partnerCode },
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })

        const { status, data } = response

        if (status === 200 && data) {
          this.finalPayload = data?.map(val => ({
            label: val.label,
            value: val.value,
            dateLastModified: val.dateLastModified,
          }))

          // Helper to extract common fields from a source item
          const getCommonFields = sourceItem => ({
            partnerCode: sourceItem?.partnerCode,
            comment: sourceItem?.value?.comment,
            email: sourceItem?.value?.uploadedBy,
            dateModified: sourceItem?.dateLastModified ? toDateWithYear(sourceItem?.dateLastModified) : null,
          })

          // Loop through the configuration to process each list using Object.keys().forEach()
          Object.keys(processingConfig).forEach(listName => {
            const configItem = processingConfig[listName]
            const sourceItem = data.find(val => val.label === configItem.sourceLabel)

            if (sourceItem) { // Only process if the source item is found
              const commonFields = getCommonFields(sourceItem)

              if (configItem.collectionPath) { // Handle collections (like board)
                const sourceCollection = sourceItem.value?.[configItem.collectionPath]
                if (Array.isArray(sourceCollection)) {
                  // Map items within the collection
                  this[listName] = sourceCollection.map(collectionItem => {
                    const mappedItem = {}
                    // Iterate over collectionFieldMap using Object.keys().forEach()
                    Object.keys(configItem.collectionFieldMap).forEach(targetKey => {
                      const sourceKey = configItem.collectionFieldMap[targetKey]
                      mappedItem[targetKey] = collectionItem?.[sourceKey]
                    })
                    // Combine mapped item properties with common fields
                    return { ...mappedItem, ...commonFields }
                  })
                } else {
                // If collection is missing or not an array, assign empty array
                  this[listName] = []
                }
              } else if (configItem.fieldMap) { // Handle single items
                const mappedObject = {}
                // Map fields from the source item's value using Object.keys().forEach()
                Object.keys(configItem.fieldMap).forEach(targetKey => {
                  const sourceKey = configItem.fieldMap[targetKey]
                  mappedObject[targetKey] = sourceItem.value?.[sourceKey]
                })
                // Combine mapped object properties with common fields and wrap in an array
                this[listName] = [{ ...mappedObject, ...commonFields }]
              } else {
                // Should not happen with the current config, but good practice
                this[listName] = []
              }
            } else {
            // If the source item for this label is not found, initialize the list as empty
              this[listName] = []
              console.warn(`Due diligence item with label ${configItem.sourceLabel} not found in response data.`)
            }
          }) // End Object.keys(processingConfig).forEach
        } else {
        // Handle non-200 status or missing data if necessary
          console.error('Failed to fetch due diligence data:', response)
        }
      } catch (error) {
        console.error('Error fetching or processing due diligence data:', error)
      } finally {
        this.showSpinner = false
        // Ensure the modal is shown even on error, or handle differently if needed
        this.$root.$emit('bv::show::modal', 'due-diligence-modal')
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadPfiOnboarding({
          search: this.filter,
          page: 1,
          limit: 100000,
          fileExportFormat: 1,
          permission: lmsPermissions.EXPORT_PFI_ONBOARDING,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'PFI Onboarding Report.csv')
          this.alertSuccess({ text: 'Your request to download PFI Onboarding Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadPfiOnboarding({
          search: this.filter,
          page: 1,
          limit: 100000,
          fileExportFormat: 0,
          permission: lmsPermissions.EXPORT_PFI_ONBOARDING,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
          FileSaver.saveAs(blob, 'PFI Onboarding Report.xlsx')
          this.alertSuccess({ text: 'Your request to download PFI Onboarding Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async pdfExport() {
      try {
        this.isBusy = true
        const response = await this.downloadPfiOnboarding({
          search: this.filter,
          page: 1,
          limit: 100000,
          fileExportFormat: 2,
          permission: lmsPermissions.EXPORT_PFI_ONBOARDING,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, 'PFI Onboarding Report.pdf')
          this.alertSuccess({ text: 'Your request to download PFI Onboarding Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async onSelectYearAppointed(dateStr) {
      const selectedDateStr = Array.isArray(dateStr) ? dateStr[0] : dateStr

      // Call the helper function to calculate the duration string
      const durationString = calculateDurationString(selectedDateStr)

      this.dueDiligence.compulsoryDocument.boardManagement.duration = durationString
    },
    // Helper to safely get nested property values
    getNestedProperty(obj, path) {
      if (!path) return obj
      const pathParts = path.split('.')
      return pathParts.reduce((current, part) => current?.[part], obj)
    },

    // Helper to safely set nested property values
    setNestedProperty(obj, path, value) {
      if (!path) return
      const pathParts = path.split('.')
      let current = obj
      for (let i = 0; i < pathParts.length - 1; i += 1) {
        if (current === null || typeof current !== 'object') {
          console.error(`Cannot set property: Path "${path}" is invalid at "${pathParts.slice(0, i + 1).join('.')}"`)
          return
        }
        if (!current[pathParts[i]] && i < pathParts.length - 1) {
          console.error(`Cannot set property: Path "${path}" does not exist at "${pathParts.slice(0, i + 1).join('.')}"`)
          return
        }
        current = current[pathParts[i]]
      }
      if (current !== null && typeof current === 'object') {
        current[pathParts[pathParts.length - 1]] = value
      } else {
        console.error(`Cannot set property: Parent path "${pathParts.slice(0, -1).join('.')}" is not an object or is null.`)
      }
    },

    // Helper to extract common fields from the response data returned by saveOrUpdateDueDiligence
    getCommonFieldsFromResponseData(data) {
      return {
        partnerCode: data?.partnerCode,
        comment: data?.value?.comment,
        email: data?.value?.uploadedBy,
        dateModified: data?.dateLastModified ? toDateWithYear(data?.dateLastModified) : null,
      }
    },

    // --- Generic Save Method ---
    // This method contains the common logic for saving due diligence items.
    async saveDueDiligenceItem(configName) {
      const config = saveConfig[configName]

      if (!config) {
        console.error(`Save configuration not found for: ${configName}`)
        this.alertError({ text: this.$t('An error occurred while saving.') })
        return
      }

      const validator = this.$refs[config.validationRef]

      if (!validator) {
        console.error(`Validation ref "${config.validationRef}" not found.`)
        this.alertError({ text: this.$t('An internal error occurred.') })
        return
      }

      validator.validate().then(async success => {
        if (success) {
          this.confirm(async () => {
            try {
              this.showSpinner = true

              // Determine the base source object (supportingDocument or compulsoryDocument)
              const resetPathParts = config.resetPath.split('.')
              const sourceDocKey = resetPathParts[0] // 'supportingDocument' or 'compulsoryDocument'
              const sourceDataObject = this.getNestedProperty(this.dueDiligence, sourceDocKey)

              if (!sourceDataObject) {
                console.error(`Base source object "${sourceDocKey}" not found on dueDiligence.`)
                this.alertError({ text: this.$t('An internal error occurred (source data missing).') })
                this.showSpinner = false
                return
              }

              const payload = {
                label: config.label,
                value: config.payloadValue(sourceDataObject), // Pass the base source object
                partnerCode: this.partnerCode,
              }

              this.showSpinnerDoc = true
              const response = await this.saveOrUpdateDueDiligence(payload)
              const { status, data } = response
              if (status === 200 && data) {
                this.showSpinnerDoc = false
                const listConfig = processingConfig[config.processingConfigKey]
                const newPayload = {
                  label: data.label,
                  value: data.value,
                }
                const existingIndex = this.finalPayload.findIndex(item => item.label === newPayload.label)
                if (existingIndex !== -1) {
                  this.finalPayload.splice(existingIndex, 1, newPayload)
                } else {
                  this.finalPayload.push(newPayload)
                }

                if (!listConfig) {
                  console.error(`No list mapping configuration found in processingConfig for key "${config.processingConfigKey}". List not updated.`)
                  this.onViewDueDiligence({ partnerCode: this.partnerCode }) // Fallback
                } else if (config.updateType === 'collection' && listConfig.collectionPath) {
                  // --- Handle updating a collection list (like board) ---
                  const sourceCollection = data.value?.[listConfig.collectionPath]

                  if (Array.isArray(sourceCollection)) {
                    const commonFields = this.getCommonFieldsFromResponseData(data)
                    this[config.listProperty] = sourceCollection.map(collectionItem => {
                      const mappedItem = {}
                      Object.keys(listConfig.collectionFieldMap).forEach(targetKey => {
                        const sourceKey = listConfig.collectionFieldMap[targetKey]
                        mappedItem[targetKey] = collectionItem?.[sourceKey]
                      })

                      return { ...mappedItem, ...commonFields }
                    })
                  } else {
                    console.error(`Response data for "${config.label}" did not contain a valid collection array at "${listConfig.collectionPath}". List not updated.`)
                    this[config.listProperty] = []
                    this.onViewDueDiligence({ partnerCode: this.partnerCode }) // Fallback
                  }
                } else if (listConfig.fieldMap) {
                // --- Handle updating a standard single item list ---
                  const updatedListItem = this.getCommonFieldsFromResponseData(data)
                  Object.keys(listConfig.fieldMap).forEach(targetKey => {
                    const sourceKey = listConfig.fieldMap[targetKey]
                    updatedListItem[targetKey] = data.value?.[sourceKey]
                  })
                  // Assign the new item (wrapped in an array) to the correct list property
                  // NOTE: Corrected original bug where some lists were assigned an object instead of an array
                  this[config.listProperty] = [updatedListItem]
                } else {
                  console.error(`Processing config for "${config.processingConfigKey}" has neither fieldMap nor collectionPath.`)
                  this.onViewDueDiligence({ partnerCode: this.partnerCode }) // Fallback
                }

                // Reset the source data in the form using the full resetPath
                this.setNestedProperty(this.dueDiligence, config.resetPath, config.resetValue)

                this.alertSuccess({ text: this.$t(config.successMessageKey) })
              } else {
                const errorMessage = data?.message || this.$t('Failed to save due diligence item.')
                this.alertError({ text: errorMessage })
                console.error('Save failed:', response)
              }
            } catch (error) {
              console.error(`Error saving ${config.label}:`, error)
              this.alertError({ text: this.$t('An error occurred while saving.') })
            } finally {
              this.showSpinner = false
              this.showSpinnerDoc = false
            }
          }, { text: config.confirmMessage })
        } else {
          const errorMessages = []
          Object.keys(validator.errors).forEach(key => {
            const value = validator.errors[key]

            if (Array.isArray(value) && value.length > 0) {
              value.forEach(error => {
                errorMessages.push(`${error}`)
              })
            }
          })
          this.allValidationErrors = errorMessages
          this.$root.$emit('bv::show::modal', 'all-validated-modal')
        }
      })
    },
    async saveLegalRegDocuments() {
      await this.saveDueDiligenceItem('saveLegalRegDocuments')
    },

    async saveLegalGovernance() {
      await this.saveDueDiligenceItem('saveLegalGovernance')
    },

    async saveBoardManagementInfo() {
      await this.saveDueDiligenceItem('saveBoardManagementInfo')
    },

    async saveFinancialStatements() {
      await this.saveDueDiligenceItem('saveFinancialStatements')
    },

    async saveRecentFinancialPerformance() {
      await this.saveDueDiligenceItem('saveRecentFinancialPerformance')
    },

    async saveRiskManagementFramework() {
      await this.saveDueDiligenceItem('saveRiskManagementFramework')
    },
    async saveCreditRiskManagement() {
      await this.saveDueDiligenceItem('saveCreditRiskManagement')
    },
    async saveLoanPortfolioDetails() {
      await this.saveDueDiligenceItem('saveLoanPortfolioDetails')
    },

    async saveAMLCFTCompliance() {
      await this.saveDueDiligenceItem('saveAMLCFTCompliance')
    },

    async saveRiskAndAuditStructure() {
      await this.saveDueDiligenceItem('saveRiskAndAuditStructure')
    },

    async saveOngoingRiskMonitoring() {
      await this.saveDueDiligenceItem('saveOngoingRiskMonitoring')
    },

    async saveInternalAuditFindings() {
      await this.saveDueDiligenceItem('saveInternalAuditFindings')
    },

    async saveCorporateGovernance() {
      await this.saveDueDiligenceItem('saveCorporateGovernance')
    },

    async saveCollateralManagement() {
      // Removed the inner try/catch as the generic method already has it
      await this.saveDueDiligenceItem('saveCollateralManagement')
    },

    async saveCreditBureau() {
      await this.saveDueDiligenceItem('saveCreditBureau')
    },

    async saveCustomerComplaintsHandling() {
      await this.saveDueDiligenceItem('saveCustomerComplaintsHandling')
    },

    async saveConsumerCreditProgram() {
      await this.saveDueDiligenceItem('saveConsumerCreditProgram')
    },

    async saveConsumerLoanPricing() {
      await this.saveDueDiligenceItem('saveConsumerLoanPricing')
    },

    async saveRatingAgencyReports() {
      await this.saveDueDiligenceItem('saveRatingAgencyReports')
    },

    async saveExternalBoardAssessment() {
      await this.saveDueDiligenceItem('saveExternalBoardAssessment')
    },

    async saveITSystemAgreement() {
      await this.saveDueDiligenceItem('saveITSystemAgreement')
    },

    async saveTaxDeductionRemittances() {
      await this.saveDueDiligenceItem('saveTaxDeductionRemittances')
    },

    async saveRegulatoryCompliance() {
      await this.saveDueDiligenceItem('saveRegulatoryCompliance')
    },

    async saveRegulatoryExaminationReports() {
      await this.saveDueDiligenceItem('saveRegulatoryExaminationReports')
    },

    async saveInternalRiskMonitoring() {
      await this.saveDueDiligenceItem('saveInternalRiskMonitoring')
    },
    async saveEnvironmentAndSocialRisk() {
      await this.saveDueDiligenceItem('saveEnvironmentAndSocialRisk')
    },
    async saveAdvancedRiskAnalysis() {
      await this.saveDueDiligenceItem('saveAdvancedRiskAnalysis')
    },

    async dueDiligenceSubmit() {
      const completenessCheck = checkDueDiligenceCompleteness(
        this.finalPayload,
        DUE_DILIGENCE_ENUMS,
      )

      if (!completenessCheck.isComplete) {
        const missingCount = completenessCheck.missingLabels.length
        const formattedMissingList = completenessCheck.missingLabels
          .map(label => addSpacesToLabel(label)).sort()
          .join('<br> ')

        const errorMessage = `Cannot submit Due Diligence. <br>${missingCount} required document(s) missing: <br> <br>${formattedMissingList}. <br> <br>Please ensure all compulsory and supporting documents sections are completed.`

        this.alertError({ html: this.$t(errorMessage) })

        return
      }

      const payload = {
        dueDiligenceItems: this.finalPayload,
        partnerCode: this.partnerCode,
      }

      try {
        this.showSpinner = true

        const response = await this.submitDueDiligence(payload)
        const { status } = response

        if (status === 200) {
          this.hideModal('due-diligence-modal')
          this.alertSuccess({ text: this.$t('Your request to submit Due Diligence was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },

    async handleDueDiligenceSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.confirm(this.dueDiligenceSubmit, { text: 'Are you sure you want to submit the due diligence form?' })
    },

    resetFilters() {
      this.filter.institutionName = ''
      this.filter.email = ''
      this.filter.partnerCode = ''
    },
    resetCreditLimitFilter() {
      this.creditLimitList = []
      this.creditLimitFormData.creditLimit = ''
      this.creditLimitButton = 'Save'

      if (this.$refs.creditLimitComponent) {
        this.$refs.creditLimitComponent.reset()
      }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchPfiOnboarding()
    },
    resetDocumentModal() {
      this.viewDocumentData = ''
      this.viewDocumentModalTitle = ''
      this.showSpinnerDoc = false
    },
  },
}
</script>

<style>
.nav-tabs .nav-link:after {
    background:  var(--primary) !important;
  }
</style>
<style lang="scss" scoped>
.custom-checkbox:last-child{
    margin-left: 20px !important;

}
.upload-image{
    border: 2px dashed #DCD9D9;
    min-height: 250px;
    position: relative;
    width: 100%;
}
.horizontal-line {
  position: relative;
  border: 1px dashed #DCD9D9;
}
.format {
    color: #999999 !important;
}
form input[type=file]{
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}
.upload-instruction{
  padding-top: 22%;
  color: #000;
  text-align: center;
}
.trash {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-right: 10px;
  cursor: pointer;
}
.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}
.upload-label {
  color: #5e5873;
  font-size: 0.857rem;
  margin-bottom: 0.2857rem;
}
// .upload-id-photo {
//   width: 300px;
//   height: 300px;
// }
.form-section {
  padding-bottom: 15px;
  margin-bottom: 15px;
}

.form-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.upload-id-photo {
  max-width: 300px;
  margin: 0 auto;
}
.text-primary {
  color: var(--primary) !important
}
</style>
